import React, { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams, useNavigate, useLocation, Link } from "react-router-dom";
import { getCountrySlug } from "@/lib/countryUtils";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, FileText, DollarSign, TrendingUp, Building, AlertTriangle, ArrowLeft, Info, ChevronDown, ChevronRight, MapPinned, ExternalLink, Share2, Loader2 } from "lucide-react";
import { ShareDignityModal } from "@/components/ShareDignityModal";
import { BucketCharts } from "@/components/BucketCharts";
import { DignityEducationTab } from "@/components/dignity/DignityEducationTab";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, Cell } from "recharts";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useBrand } from "@/config/brands";
import { ManualNormalizationFlag, QuickFlagButton } from "@/components/ManualNormalizationFlag";
import { OutlierFlagButton } from "@/components/OutlierFlagButton";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import EducationalContentRenderer from "@/components/EducationalContentRenderer";
import { HospitalPricingMap } from "@/components/HospitalPricingMap";
import { useQuery } from "@tanstack/react-query";
import { loadCodeDetail, type CodeDetail } from "@/lib/codeDetailStore";
import { loadTranslatedStaticData } from "@/lib/offlineData";
import { ScenarioCard, type ScenarioCardData, type CardTheme } from "@/components/ScenarioCard";
import { useTheme } from "@/contexts/ThemeContext";
import { useTranslation } from "react-i18next";

// Educational context for code types that need extra explanation
const CODE_TYPE_EDUCATION: Record<string, { title: string; explanation: string; priceNote: string }> = {
  revenue: {
    title: 'Understanding Revenue Codes',
    explanation: 'Revenue codes are hospital billing categories, not individual procedures. When a hospital files a claim, every line item is assigned a revenue code to indicate which department provided the service. For example, Revenue Code 0450 (Emergency Room) covers all charges billed through the ER department \u2014 from a simple triage assessment to critical care interventions, IV medications, and diagnostic tests.',
    priceNote: 'The wide price ranges you see reflect this bundling: a single revenue code may include hundreds of different services at vastly different price points. The prices shown represent what hospitals charge for individual line items categorized under this revenue code, not the total cost of a visit to that department.',
  },
  drg: {
    title: 'Understanding DRG Codes',
    explanation: 'Diagnosis-Related Group (DRG) codes represent a complete inpatient hospital stay bundled into a single payment category. Medicare and most insurers pay hospitals a fixed amount per DRG, regardless of the actual services provided. Each DRG groups patients with similar diagnoses and treatment complexity.',
    priceNote: 'The prices shown represent what hospitals charge for the entire admission under this DRG. Price variation reflects differences in hospital pricing strategies, regional cost differences, and case complexity within the same DRG category.',
  },
};

// Configuration for different code types
interface CodeTypeConfig {
  codeType: string;
  codeTypeUpper: string;
  codeFieldName: string;
  descriptionFieldName: string;
  pricingTable: string;
  procedureType: string;
  displayName: string;
  pluralName: string;
  description: string;
  chargeFieldNames?: {
    avg: string;
    median: string;
    min: string;
    max: string;
  };
}

const CODE_TYPE_CONFIGS: Record<string, CodeTypeConfig> = {
  cpt: {
    codeType: 'cpt',
    codeTypeUpper: 'CPT',
    codeFieldName: 'cpt_code',
    descriptionFieldName: 'procedure_description',
    pricingTable: 'cpt_pricing_averages',
    procedureType: 'cpt',
    displayName: 'CPT Code',
    pluralName: 'CPT Codes',
    description: 'Current Procedural Terminology (CPT) codes are medical procedure codes maintained by the American Medical Association. They describe medical, surgical, and diagnostic services and are used by physicians and healthcare providers for billing.'
  },
  drg: {
    codeType: 'drg',
    codeTypeUpper: 'DRG',
    codeFieldName: 'drg_code',
    descriptionFieldName: 'procedure_description',
    pricingTable: 'drg_pricing_averages',
    procedureType: 'drg',
    displayName: 'DRG Code',
    pluralName: 'DRG Codes',
    description: 'Diagnosis Related Group (DRG) codes are a patient classification system that standardizes prospective payment to hospitals and encourages cost containment initiatives.'
  },
  revenue: {
    codeType: 'revenue',
    codeTypeUpper: 'REV',
    codeFieldName: 'revenue_code',
    descriptionFieldName: 'revenue_description',
    pricingTable: 'revenue_pricing_averages',
    procedureType: 'revenue',
    displayName: 'Revenue Code',
    pluralName: 'Revenue Codes',
    description: 'Revenue codes are 4-digit numbers that are used on hospital bills to tell the insurance companies either where the patient was when they received treatment or what type of item a patient might have received as a patient.',
    chargeFieldNames: {
      avg: 'avg_charge',
      median: 'median_charge',
      min: 'min_charge',
      max: 'max_charge'
    }
  },
  drug: {
    codeType: 'drug',
    codeTypeUpper: 'NDC',
    codeFieldName: 'drug_code',  // drug_pricing_averages uses 'drug_code' not 'ndc_code'
    descriptionFieldName: 'drug_name',
    pricingTable: 'drug_pricing_averages',
    procedureType: 'ndc',  // Database uses 'ndc' as procedure_type for all drug codes
    displayName: 'NDC Code',
    pluralName: 'Drug Codes',
    description: 'National Drug Code (NDC) is a unique product identifier used in the United States for drugs intended for human use.',
    chargeFieldNames: {
      avg: 'avg_charge',
      median: 'median_charge',
      min: 'min_charge',
      max: 'max_charge'
    }
  },
  hcpcs: {
    codeType: 'hcpcs',
    codeTypeUpper: 'HCPCS',
    codeFieldName: 'hcpcs_code',
    descriptionFieldName: 'procedure_description',
    pricingTable: 'hcpcs_pricing_averages',
    procedureType: 'hcpcs',
    displayName: 'HCPCS Code',
    pluralName: 'HCPCS Codes',
    description: 'Healthcare Common Procedure Coding System (HCPCS) codes are used to identify products, supplies, and services not included in CPT codes, such as ambulance services, durable medical equipment, and prosthetics.'
  },
  ndc: {
    codeType: 'ndc',
    codeTypeUpper: 'NDC',
    codeFieldName: 'drug_code',  // drug_pricing_averages uses 'drug_code' not 'ndc_code'
    descriptionFieldName: 'drug_name',
    pricingTable: 'drug_pricing_averages',
    procedureType: 'ndc',
    displayName: 'NDC Code',
    pluralName: 'NDC Codes',
    description: 'National Drug Code (NDC) is a unique product identifier used in the United States for drugs intended for human use.',
    chargeFieldNames: {
      avg: 'avg_charge',
      median: 'median_charge',
      min: 'min_charge',
      max: 'max_charge'
    }
  }
};

interface ProcedureCodeData {
  procedure_code: string;
  procedure_description: string;
  consumer_friendly_description?: string;
  total_hospitals: number;
  avg_gross_charge: number;
  median_gross_charge: number;
  min_gross_charge: number;
  max_gross_charge: number;
  avg_discounted_cash: number;
  price_variation: number;
}

interface HospitalPricing {
  hospital_id: string;
  hospital_name: string;
  gross_charge: number;
  original_gross_charge?: number;
  has_normalization?: boolean;
  normalization_factor?: number;
  discounted_cash_price: number;
  min_negotiated_rate: number;
  max_negotiated_rate: number;
  inpatient_outpatient: string;
  city: string;
  state: string;
  latitude?: number;
  longitude?: number;
  drug_unit_quantity?: number;
  drug_unit_type?: string;
  normalized_unit?: string;
  unit_quantity?: number;
  procedure_description?: string;
}

interface DosageGroup {
  dosage_quantity: number;
  dosage_unit: string;
  setting_type?: string; // For setting groups: inpatient/outpatient/both/unspecified
  unit_quantity?: number; // The actual quantity from the data (e.g., 1, 5, 10)
  hospital_count: number;
  min_price: number;
  max_price: number;
  avg_price: number;
  median_price: number;
  price_per_unit: number;
  hospitals: HospitalPricing[];
}

interface InternationalPricing {
  country: string;
  countryCode: string;
  flag: string;
  currency: string;
  price: number;
  priceUSD: number;
  healthcareSystem: string;
  coverage: string;
  source: string;
  lastUpdated: string;
  hasRealData: boolean;
  foreignCode?: string;
  consumerCostNotes?: string;
}

interface ProcedureCodePageProps {
  codeType?: 'cpt' | 'drg' | 'hcpcs' | 'revenue' | 'drug';
  /** When set, render this code instead of reading it from the URL — used when the
   *  page is embedded inside a scenario's native code modal (keeps breadcrumbs). */
  codeOverride?: string;
  /** Embedded in a modal: drop the page's own Helmet + back-to-home chrome. */
  embedded?: boolean;
}

const ProcedureCodePage = ({ codeType = 'cpt', codeOverride, embedded = false }: ProcedureCodePageProps) => {
  const config = CODE_TYPE_CONFIGS[codeType];
  const { t } = useTranslation(['codes', 'common']);
  // Map codeType to i18n key prefix (revenue -> rev to match codes.json)
  const i18nCodeType = codeType === 'revenue' ? 'rev' : codeType;
  const params = useParams();
  const code = codeOverride ?? params.code;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Back control: if the user arrived here from somewhere inside the app
  // (location.key is only 'default' on the very first/loaded entry), go back in
  // history so they return exactly where they were — e.g. the scenario's Code
  // Breakdown modal (?section=code-breakdown). Only fall back to home for direct
  // loads / deep links where there's no in-app history to return to.
  const goBack = () => {
    if (location.key && location.key !== 'default') navigate(-1);
    else navigate('/');
  };

  // True when this page is rendered inside an <iframe>. Embeds (e.g. partner
  // sites linking to /drg/894) shouldn't get the back-to-home control because
  // it would navigate the iframe away from us — they have their own host nav.
  // Cross-origin parent access throws, so the catch falls back to "treat as
  // embedded" since same-origin iframes succeed and same-origin top-level
  // pages also succeed (window.self === window.top).
  const isEmbedded = useMemo(() => {
    if (typeof window === 'undefined') return false;
    try { return window.self !== window.top; } catch { return true; }
  }, []);

  // Scroll to URL hash anchor (e.g. #variations) once data has loaded
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash.replace('#', '');
    if (!hash || !code) return;
    // Wait until the page has rendered the anchor element
    const tryScroll = (attempts: number) => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (attempts < 20) {
        setTimeout(() => tryScroll(attempts + 1), 200);
      }
    };
    tryScroll(0);
  }, [code]);

  const [procedureCodes, setProcedureCodes] = useState<ProcedureCodeData[]>([]);
  const [selectedCode, setSelectedCode] = useState<ProcedureCodeData | null>(null);
  const [procedureInfo, setProcedureInfo] = useState<{name?: string, description?: string, consumer_friendly_description?: string} | null>(null);
  const [internationalCurrentPage, setInternationalCurrentPage] = useState(1);
  const internationalItemsPerPage = 3;
  const [hospitalPricing, setHospitalPricing] = useState<HospitalPricing[]>([]);
  const [settingGroups, setSettingGroups] = useState<DosageGroup[]>([]); // Groups by setting (inpatient/outpatient/unspecified)
  const [dosageGroups, setDosageGroups] = useState<DosageGroup[]>([]); // Groups by dosage/unit
  const [groupByDosage, setGroupByDosage] = useState<boolean>(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [groupPages, setGroupPages] = useState<Record<string, number>>({}); // pagination per expanded group
  const HOSPITALS_PER_PAGE = 25;
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [settingFilter, setSettingFilter] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(false);
  const [hasDosageData, setHasDosageData] = useState(false);
  const [isInternationalExpanded, setIsInternationalExpanded] = useState(false);
  const [intlPage, setIntlPage] = useState(0); // pagination — 5 rows per page when collapsed
  const [shareOpen, setShareOpen] = useState(false);

  // Right-click anywhere on the page (outside form inputs) opens the share modal pre-loaded with
  // this code's context. Lets users share an /cpt /hcpcs /drg etc. page from the OS context menu
  // pattern they already know. Skips inside <input> / <textarea> / <select> so right-click on
  // form fields still gets the native paste/cut/etc menu.
  useEffect(() => {
    if (!code) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      // Allow shift-right-click for browser default menu (debugger / inspect)
      if (e.shiftKey) return;
      e.preventDefault();
      setShareOpen(true);
    };
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, [code]);
  const [internationalPrices, setInternationalPrices] = useState<InternationalPricing[]>([]);
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();

  // ScenarioCard theme
  const cardTheme: CardTheme = {
    cardBg: isDark ? '#0f1a2e' : '#FFFFFF',
    border: isDark ? '#1e3a5f' : '#e2e8f0',
    shadow: isDark ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,40,104,0.08)',
    red: '#B22234', gold: '#C5A55A', green: '#2b5e49', blue: '#002868',
    text: isDark ? '#f0ebe3' : '#1B1B1B',
    textMuted: isDark ? '#9ca3af' : '#6b7280',
  };

  // Brand-conditional style helpers.
  // Hero accent — lifts off the page background. The base navy (#002868)
  // disappears against the dark-mode navy-blue body bg (reported on
  // /rev/0360 in dark mode), so we swap to AHP gold there. Light mode keeps
  // the navy. Use this anywhere a heading/icon sits on the page bg (NOT on
  // a white card — cards have their own contrast).
  const heroAccentColor = isDark ? '#C5A55A' : '#002868';
  const headingFont = isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: '#1B1B1B' } : {};
  const bodyFont = isAhp ? { fontFamily: "'Instrument Sans', system-ui, sans-serif" } : {};
  const sectionBg = isAhp ? 'bg-[#002868]/10 border-[#002868]/20' : 'bg-gray-800 border-gray-700';
  const sectionText = isAhp ? 'text-[#1B1B1B]' : 'text-white';
  const sectionSubtext = isAhp ? 'text-[#1B1B1B]/70' : 'text-gray-300';
  const expandedHeaderBg = isAhp ? 'bg-[#002868] border-t-2 border-[#002868]' : 'bg-gray-800 border-t-2 border-gray-600';
  const expandedHeaderText = isAhp ? 'text-white' : 'text-gray-300';

  // Map route code type to store code type
  const storeCodeType = codeType === 'revenue' ? 'rev' : codeType === 'drug' ? 'ndc' : codeType;

  // Fast path: load static code detail from R2 CDN (cached in IndexedDB)
  const { data: staticDetail } = useQuery({
    queryKey: ['code-detail-static', storeCodeType, code],
    queryFn: () => loadCodeDetail(storeCodeType, code!),
    enabled: !!code,
    staleTime: Infinity,
  });

  // Hospital finance enrichment for the right-pane scatter chart on expanded buckets.
  // Pulls bed_count + latest total_revenue_usd + max CEO comp for every hospital that
  // reports this code. Lets us compute revenue-per-bed + correlate against price.
  // Bulk-paged to avoid IN-clause URL length limits (some codes report at 5,000+ hospitals).
  const allHospitalIds = useMemo(() => {
    if (!staticDetail?.hospitals) return [] as string[];
    return Array.from(new Set(staticDetail.hospitals.map((h: any) => h.id).filter(Boolean)));
  }, [staticDetail]);

  const { data: financeMap } = useQuery({
    queryKey: ['code-finance-enrichment', code, allHospitalIds.length],
    enabled: allHospitalIds.length > 0,
    staleTime: 1000 * 60 * 30,
    queryFn: async () => {
      // Bulk fetch hospitals.bed_count + hospital_system, then latest hospital_finances row per hospital,
      // then max CEO comp per hospital. Returned as a Map<hospital_id, {bed_count, total_revenue, ceo_comp, system}>.
      const map = new Map<string, { bed_count?: number; total_revenue?: number; system?: string; ceo_comp?: number }>();
      // ~150 UUIDs ≈ 5.6KB of query string — the API 414s past ~8KB, and the
      // 414 carries no CORS headers so the browser misreports it as a CORS
      // failure. 500 per batch silently killed this enrichment for every
      // code with a few hundred reporting hospitals.
      const PAGE = 150;
      for (let i = 0; i < allHospitalIds.length; i += PAGE) {
        const batch = allHospitalIds.slice(i, i + PAGE);
        const [{ data: hRows }, { data: fRows }, { data: ceRows }] = await Promise.all([
          supabase.from('hospitals').select('id, bed_count, hospital_system').in('id', batch),
          supabase.from('hospital_finances').select('hospital_id, fiscal_year, total_revenue_usd')
            .in('hospital_id', batch).eq('data_source', 'HCRIS')
            .order('fiscal_year', { ascending: false }),
          supabase.from('hospital_990_executives').select('hospital_id, total_comp_usd')
            .in('hospital_id', batch)
            .order('total_comp_usd', { ascending: false, nullsFirst: false }),
        ]);
        if (hRows) for (const r of hRows as any[]) {
          const ex = map.get(r.id) || {};
          map.set(r.id, { ...ex, bed_count: r.bed_count || undefined, system: r.hospital_system || undefined });
        }
        if (fRows) {
          // First row per hospital (already DESC by fiscal_year) wins
          const seen = new Set<string>();
          for (const r of fRows as any[]) {
            if (seen.has(r.hospital_id)) continue;
            seen.add(r.hospital_id);
            if (r.total_revenue_usd != null) {
              const ex = map.get(r.hospital_id) || {};
              map.set(r.hospital_id, { ...ex, total_revenue: Number(r.total_revenue_usd) });
            }
          }
        }
        if (ceRows) {
          const seen = new Set<string>();
          for (const r of ceRows as any[]) {
            if (seen.has(r.hospital_id) || r.total_comp_usd == null) continue;
            seen.add(r.hospital_id);
            const ex = map.get(r.hospital_id) || {};
            map.set(r.hospital_id, { ...ex, ceo_comp: Number(r.total_comp_usd) });
          }
        }
      }
      return map;
    },
  });

  // Fetch educational HTML for selected code (fallback when static data missing)
  const { data: educationalHtml } = useQuery({
    queryKey: ['educational-html', code, config.codeTypeUpper],
    queryFn: async () => {
      if (!code) return null;
      // If static detail has educational HTML, use that
      if (staticDetail?.educationalHtml) return staticDetail.educationalHtml;

      const { data, error } = await supabase
        .from('procedures')
        .select('procedure_details_html')
        .eq('code', code)
        .eq('code_type', config.codeTypeUpper)
        .limit(1)
        .maybeSingle();

      if (error || !data) return null;
      return data.procedure_details_html;
    },
    // Only query Supabase when static data explicitly returned null (not found on R2)
    enabled: !!code && staticDetail === null,
  });

  // Fetch related scenarios that use this procedure code (full card data)
  const { data: relatedScenarios } = useQuery({
    queryKey: ['related-scenarios-for-code', code, config.procedureType],
    queryFn: async () => {
      if (!code) return [];
      // Slim DB query: just the scenario ids that use this code. Card data
      // (with REAL calculated costs — never seeded) comes from the static
      // scenarios index, same source as the browse grid. (The old inline
      // scenarios!inner select referenced columns that don't exist —
      // calculated_avg_cost/phase_breakdown live in the index, not the
      // scenarios table — so this widget 400'd and silently hid itself.)
      const [{ data, error }, indexJson] = await Promise.all([
        supabase
          .from('scenario_procedures')
          .select('scenario_id, scenarios!inner(is_active)')
          .eq('code', code)
          .eq('code_type', config.codeTypeUpper)
          .eq('scenarios.is_active', true)
          .limit(20),
        loadTranslatedStaticData<{ data: any[] }>('scenarios_index.json').catch(() => null),
      ]);
      if (error || !data || !indexJson?.data) return [];

      const byId = new Map<string, any>(indexJson.data.map((s: any) => [s.id, s]));
      const seen = new Set<string>();
      const out: ScenarioCardData[] = [];
      for (const row of data as any[]) {
        if (seen.has(row.scenario_id)) continue;
        seen.add(row.scenario_id);
        const s = byId.get(row.scenario_id);
        if (!s || s.hidden) continue;
        out.push({
          id: s.id,
          name: s.name,
          slug: s.slug,
          category: s.category,
          description: s.description || '',
          icon_name: s.iconName || 'Activity',
          color: s.color || '#002868',
          duration: s.duration || '',
          total_procedures: s.totalProcedures || 0,
          phases: Array.isArray(s.phases) ? s.phases : Object.keys(s.phaseBreakdown || {}),
          severity_level: s.severityLevel ?? null,
          calculated_avg_cost: s.estimatedCost || 0,
          calculated_median_cost: s.estimatedCostMedian ?? undefined,
          phaseBreakdown: s.phaseBreakdown || {},
          phaseBreakdownMedian: s.phaseBreakdownMedian ?? null,
          phaseProcedures: {},
          tags: s.tags || [],
          created_by: s.createdBy || null,
          is_verified: !!s.isVerified,
        } as ScenarioCardData);
      }
      return out;
    },
    enabled: !!code,
    staleTime: 5 * 60 * 1000,
  });

  // Simple related scenarios shape for DignityEducationTab (just id/name/slug/category)
  const relatedScenariosSimple = relatedScenarios?.map(s => ({
    id: s.id, name: s.name, slug: s.slug, category: s.category,
  }));

  // Helper function to normalize unit values to avoid splitting groups
  const normalizeUnit = (unit: string | null | undefined): string => {
    if (!unit || unit.trim() === '' || unit.toLowerCase() === 'unspecified' || unit.toLowerCase() === 'other') {
      return 'unspecified';
    }
    return unit.trim().toLowerCase();
  };

  // Helper function to normalize setting type values
  const normalizeSetting = (setting: string | null | undefined): string => {
    if (!setting || setting.trim() === '') {
      return 'unspecified';
    }
    const normalized = setting.trim().toLowerCase();
    // Normalize all variations to canonical values
    if (normalized === 'inpatient' || normalized === 'inpatient_care' || normalized === 'in') {
      return 'inpatient';
    }
    if (normalized === 'outpatient' || normalized === 'outpatient_care' || normalized === 'out') {
      return 'outpatient';
    }
    if (normalized === 'both' || normalized === 'both_settings' || normalized === 'inpatient/outpatient') {
      return 'both';
    }
    return 'unspecified';
  };

  // Helper functions for dosage group management
  const toggleGroupExpansion = (dosageKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(dosageKey)) {
      newExpanded.delete(dosageKey);
    } else {
      newExpanded.add(dosageKey);
    }
    setExpandedGroups(newExpanded);
    // Reset page to 0 when toggling
    setGroupPages(prev => ({ ...prev, [dosageKey]: 0 }));
  };

  const getDosageKey = (quantity: number, unit: string) => `${quantity}_${unit}`;

  // Auto-expand if only one group exists
  useEffect(() => {
    const activeGroups = groupByDosage ? dosageGroups : settingGroups;

    // If there's exactly one group, automatically expand it
    if (activeGroups.length === 1) {
      const group = activeGroups[0];
      const groupKey = groupByDosage
        ? getDosageKey(group.dosage_quantity, group.dosage_unit)
        : group.dosage_unit; // For settingGroups, dosage_unit stores the setting type

      setExpandedGroups(new Set([groupKey]));
    }
  }, [dosageGroups, settingGroups, groupByDosage]);

  // Helper function to format dosage display for CAR-T cells and other units
  const formatDosageDisplay = (quantity: number, unit: string) => {
    // Don't display unknown/unspecified dosages
    if (unit === 'unknown' || unit === 'other' || unit === 'unspecified') {
      return '';
    }
    if (unit === 'million_cells' || unit === 'M_CELLS') {
      return `${quantity} million cells`;
    }
    if (unit === 'mg' || unit === 'MG') {
      return `${quantity}mg`;
    }
    if (unit === 'ml' || unit === 'ML') {
      return `${quantity}ml`;
    }
    if (unit === 'mcg' || unit === 'MCG') {
      return `${quantity}mcg`;
    }
    if (unit === 'units' || unit === 'UNITS' || unit === 'UN') {
      return `${quantity} units`;
    }
    // Default fallback
    return `${quantity}${unit}`;
  };

  // Helper function to format unit display for pricing
  const formatUnitDisplay = (unit: string) => {
    if (unit === 'million_cells' || unit === 'M_CELLS') {
      return 'million cells';
    }
    if (unit === 'mg' || unit === 'MG') {
      return 'mg';
    }
    if (unit === 'ml' || unit === 'ML') {
      return 'ml';
    }
    if (unit === 'mcg' || unit === 'MCG') {
      return 'mcg';
    }
    if (unit === 'units' || unit === 'UNITS' || unit === 'UN') {
      return 'units';
    }
    return unit;
  };

  // Fetch international pricing from database for the current code
  const fetchInternationalPricing = async (procedureCode: string) => {
    try {
      // Fetch real prices from the database — get ALL countries with data
      const { data: prices, error: pricesError } = await supabase
        .from('international_procedure_prices')
        .select('*, international_countries!inner(country_name, flag_emoji, currency_code, healthcare_system, default_coverage)')
        .eq('us_code', procedureCode)
        .eq('us_code_type', config.codeTypeUpper)
        .gt('price_usd', 0);

      if (pricesError) console.error('Error fetching international prices:', pricesError);

      if (!prices || prices.length === 0) {
        setInternationalPrices([]);
        return;
      }

      // Build result from ALL countries that have actual pricing data
      const result: InternationalPricing[] = prices.map(p => {
        const country = (p as any).international_countries;
        const cc = p.country_code?.trim();
        return {
          country: country?.country_name || cc,
          countryCode: cc,
          flag: country?.flag_emoji || '',
          currency: country?.currency_code || 'USD',
          price: Number(p.price_local) || 0,
          priceUSD: Number(p.price_usd) || 0,
          healthcareSystem: country?.healthcare_system || '',
          coverage: country?.default_coverage || '',
          source: p.source_name || 'Official Fee Schedule',
          lastUpdated: String(p.source_year || ''),
          hasRealData: true,
          foreignCode: p.foreign_code || undefined,
          consumerCostNotes: p.consumer_cost_notes || undefined,
        };
      });

      // Sort by price USD ascending (cheapest first)
      result.sort((a, b) => a.priceUSD - b.priceUSD);

      setInternationalPrices(result);
    } catch (error) {
      console.error('Error fetching international pricing:', error);
    }
  };

  // Populate page state from static R2 data when available
  useEffect(() => {
    if (!staticDetail || !code) return;
    const d = staticDetail;

    // Set selectedCode from static pricing
    const p = d.pricing;
    setSelectedCode({
      procedure_code: d.code,
      procedure_description: d.description,
      consumer_friendly_description: d.consumerName || undefined,
      total_hospitals: p.totalHospitals,
      avg_gross_charge: p.avgGrossCharge || 0,
      median_gross_charge: p.medianGrossCharge || 0,
      min_gross_charge: p.minGrossCharge || 0,
      max_gross_charge: p.maxGrossCharge || 0,
      avg_discounted_cash: 0,
      price_variation: p.priceVariation || 0,
    });

    // Set procedure info
    if (d.consumerName || d.llmSummary) {
      setProcedureInfo({
        name: d.consumerName || d.description,
        description: d.llmSummary || d.description,
        consumer_friendly_description: d.consumerName,
      });
    }

    // Set international pricing from static data
    if (d.internationalPricing && d.internationalPricing.length > 0) {
      setInternationalPrices(d.internationalPricing.map(ip => ({
        country: ip.countryName,
        countryCode: ip.countryCode,
        flag: ip.flag,
        currency: 'USD',
        price: ip.priceUSD,
        priceUSD: ip.priceUSD,
        healthcareSystem: '',
        coverage: '',
        source: ip.source,
        lastUpdated: '',
        hasRealData: true,
      })));
    }

    // Build hospital pricing from full hospitals list (or legacy cheapest/mostExpensive)
    const allHosps: HospitalPricing[] = [];
    const seen = new Set<string>();
    const rawHospitals = d.hospitals || [...(d.mostExpensiveHospitals || []), ...(d.cheapestHospitals || [])];
    for (const h of rawHospitals) {
      if (seen.has(h.id)) continue;
      seen.add(h.id);
      allHosps.push({
        hospital_id: h.id,
        hospital_name: h.n,
        gross_charge: h.p,
        discounted_cash_price: 0,
        min_negotiated_rate: 0,
        max_negotiated_rate: 0,
        inpatient_outpatient: normalizeSetting(h.st) || 'unspecified',
        city: h.c,
        state: h.s,
        latitude: h.la || undefined,
        longitude: h.lo || undefined,
      });
    }
    allHosps.sort((a, b) => b.gross_charge - a.gross_charge);
    setHospitalPricing(allHosps);

    // Build setting groups from actual hospital data
    // Group hospitals by setting type
    const hospsBySetting = new Map<string, HospitalPricing[]>();
    for (const h of allHosps) {
      const st = h.inpatient_outpatient || 'unspecified';
      if (!hospsBySetting.has(st)) hospsBySetting.set(st, []);
      hospsBySetting.get(st)!.push(h);
    }

    // PREFERRED: full per-bucket breakdown (setting × unit × quantity).
    // Available for code types with unit dimensions (cpt, hcpcs, rev, ndc).
    if (p.buckets && p.buckets.length > 0) {
      const groups: DosageGroup[] = p.buckets.map(b => {
        // Find matching hospital prices for this exact (setting, unit) bucket
        const matchingHosps = (staticDetail.hospitals || []).filter((h: any) => {
          const hSetting = (h.st || '').toString();
          const hSettingNorm = hSetting === 'inpatient' || hSetting === 'I' || hSetting === 'IP' ? 'inpatient'
            : hSetting === 'outpatient' || hSetting === 'O' || hSetting === 'OP' ? 'outpatient'
            : hSetting === 'both' ? 'both' : 'unspecified';
          // setting filter — 'all_settings' matches everything
          if (b.setting !== 'all_settings' && hSettingNorm !== b.setting) return false;
          return true;
        }).map((h: any) => ({
          hospital_id: h.id,
          hospital_name: h.n,
          gross_charge: h.p,
          inpatient_outpatient: h.st || '',
          city: h.c,
          state: h.s,
          latitude: h.la,
          longitude: h.lo,
        }));
        return {
          dosage_quantity: b.unitQuantity || 1,
          dosage_unit: b.unit || 'unspecified',
          setting_type: b.setting,
          unit_quantity: b.unitQuantity,
          hospital_count: b.hospitalCount,
          min_price: b.minCharge || 0,
          max_price: b.maxCharge || 0,
          avg_price: b.avgCharge || 0,
          median_price: b.medianCharge || 0,
          price_per_unit: b.avgCharge || 0,
          hospitals: matchingHosps,
        };
      });
      // Already sorted server-side: setting → unit → qty
      setSettingGroups(groups);
    } else if (hospsBySetting.size > 1 || (p.bySettings && p.bySettings.length > 0)) {
      // FALLBACK: legacy single-dimension bySettings (no unit breakdown)
      const bySettingsLookup: Record<string, any> = {};
      if (p.bySettings) {
        for (const s of p.bySettings) bySettingsLookup[s.setting] = s;
      }

      const groups: DosageGroup[] = [];
      for (const [setting, hosps] of hospsBySetting.entries()) {
        const prices = hosps.map(h => h.gross_charge).filter(Boolean);
        const sortedPrices = [...prices].sort((a, b) => a - b);
        const bs = bySettingsLookup[setting];
        groups.push({
          dosage_quantity: 1,
          dosage_unit: 'unspecified',
          setting_type: setting,
          hospital_count: hosps.length,
          min_price: prices.length > 0 ? Math.min(...prices) : 0,
          max_price: prices.length > 0 ? Math.max(...prices) : 0,
          avg_price: bs?.avgCharge || (prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0),
          median_price: bs?.medianCharge || (sortedPrices.length > 0 ? sortedPrices[Math.floor(sortedPrices.length / 2)] : 0),
          price_per_unit: bs?.avgCharge || 0,
          hospitals: hosps,
        });
      }
      // Sort: inpatient first, then outpatient, then others
      const order = { inpatient: 0, outpatient: 1, both: 2, unspecified: 3 };
      groups.sort((a, b) => (order[a.setting_type as keyof typeof order] ?? 9) - (order[b.setting_type as keyof typeof order] ?? 9));
      setSettingGroups(groups);
    } else {
      // Single group with all hospitals
      setSettingGroups([{
        dosage_quantity: 1,
        dosage_unit: 'unspecified',
        setting_type: 'unspecified',
        hospital_count: allHosps.length,
        min_price: p.minGrossCharge || 0,
        max_price: p.maxGrossCharge || 0,
        avg_price: p.avgGrossCharge || 0,
        median_price: p.medianGrossCharge || 0,
        price_per_unit: p.avgGrossCharge || 0,
        hospitals: allHosps,
      }]);
    }
    setHasDosageData(true);
    setGroupByDosage(false);
  }, [staticDetail, code]);

  useEffect(() => {
    // Only fetch browse list from Supabase if no specific code is selected
    if (!code) {
      fetchProcedureCodes();
    }
    if (code && staticDetail === null) {
      // Only fetch from Supabase if static data explicitly not found on R2
      fetchSpecificCode(code);
      fetchInternationalPricing(code);
    }
  }, [code, staticDetail]);

  // Refetch hospital pricing when setting filter changes (only for Supabase-sourced data)
  useEffect(() => {
    if (code && selectedCode && !staticDetail) {
      fetchSpecificCode(code);
    }
  }, [settingFilter]);

  const fetchProcedureCodes = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("procedure_pricing_averages")
        .select("*")
        .eq("procedure_type", config.procedureType)
        .order("total_hospitals", { ascending: false })
        .limit(100);

      if (error) throw error;

      const processedData = data?.map(item => ({
        ...item,
        procedure_code: item.procedure_code,
        price_variation: item.max_gross_charge && item.min_gross_charge && item.avg_gross_charge
          ? ((item.max_gross_charge - item.min_gross_charge) / item.avg_gross_charge * 100)
          : 0
      })) || [];

      setProcedureCodes(processedData);
    } catch (error) {
      console.error(`Error fetching ${config.displayName}s:`, error);
      toast({
        title: "Error",
        description: `Failed to fetch ${config.displayName}s data`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSpecificCode = async (procedureCode: string) => {
    setIsLoading(true);
    try {
      // Get national average from appropriate table based on code type
      let nationalAvgData = null;
      const avgField = config.chargeFieldNames?.avg || 'avg_gross_charge';
      const medianField = config.chargeFieldNames?.median || 'median_gross_charge';
      const minField = config.chargeFieldNames?.min || 'min_gross_charge';
      const maxField = config.chargeFieldNames?.max || 'max_gross_charge';

      if (config.codeType === 'revenue') {
        // Query revenue_pricing_averages table for revenue codes
        const { data, error: avgError } = await supabase
          .from("revenue_pricing_averages")
          .select(`${avgField}, ${medianField}, ${minField}, ${maxField}, total_hospitals, ${config.descriptionFieldName}`)
          .eq(config.codeFieldName, procedureCode)
          .limit(1)
          .maybeSingle();

        if (avgError && avgError.code !== 'PGRST116') {
          console.error("Error fetching national average:", avgError);
        }
        nationalAvgData = data;
      } else if (config.codeType === 'drg') {
        // Query drg_pricing_averages table for DRG codes
        const { data, error: avgError } = await supabase
          .from("drg_pricing_averages")
          .select(`${avgField}, ${medianField}, ${minField}, ${maxField}, total_hospitals, ${config.descriptionFieldName}`)
          .eq(config.codeFieldName, procedureCode)
          .limit(1)
          .maybeSingle();

        if (avgError && avgError.code !== 'PGRST116') {
          console.error("Error fetching national average:", avgError);
        }
        nationalAvgData = data;
      } else {
        // Query procedure_pricing_averages for other code types (CPT, HCPCS)
        const { data, error: avgError } = await supabase
          .from("procedure_pricing_averages")
          .select("avg_gross_charge, median_gross_charge, min_gross_charge, max_gross_charge, total_hospitals, procedure_description")
          .eq("procedure_code", procedureCode)
          .limit(1)
          .maybeSingle();

        if (avgError && avgError.code !== 'PGRST116') {
          console.error("Error fetching national average:", avgError);
        }
        nationalAvgData = data;
      }

      // Get code variations from pricing table (aggregated by setting/unit)
      // Note: DRG codes don't have setting_type column
      const selectFields = [
        config.codeFieldName,
        config.descriptionFieldName,
        ...(config.codeType !== 'drg' ? ['setting_type'] : []),
        'normalized_unit',
        avgField,
        medianField,
        minField,
        maxField,
        'total_hospitals'
      ].join(', ');

      const { data: codeData, error: codeError } = await supabase
        .from(config.pricingTable)
        .select(selectFields)
        .eq(config.codeFieldName, procedureCode);

      if (codeError) {
        console.error(`Error fetching ${config.displayName} details:`, codeError);
        throw codeError;
      }

      if (!codeData || codeData.length === 0) {
        // Code not found
        toast({
          title: `${config.displayName} Not Found`,
          description: `${config.displayName} ${procedureCode} was not found in our database or doesn't have enough data for analysis.`,
          variant: "destructive",
        });
        setSelectedCode(null);
        setHospitalPricing([]);
        return;
      }

      // Get total hospitals: use max from all_settings rows to avoid double-counting
      // (same hospital appears in inpatient, outpatient, AND all_settings rows)
      const allSettingsRows = codeData.filter((v: any) => v.setting_type === 'all_settings');
      const totalHospitals = allSettingsRows.length > 0
        ? Math.max(...allSettingsRows.map(v => v.total_hospitals || 0))
        : Math.max(...codeData.map(v => v.total_hospitals || 0));

      // Calculate weighted averages based on hospital counts (across all variations)
      const totalHospitalsForWeighting = codeData.reduce((sum, v) => sum + (v.total_hospitals || 0), 0);
      const avgGrossCharge = totalHospitalsForWeighting > 0
        ? codeData.reduce((sum, v) => sum + ((v as any)[avgField] || 0) * (v.total_hospitals || 0), 0) / totalHospitalsForWeighting
        : 0;

      // Get overall min/max across all variations
      const allMinPrices = codeData.map(v => (v as any)[minField] || 0).filter(p => p > 0);
      const allMaxPrices = codeData.map(v => (v as any)[maxField] || 0).filter(p => p > 0);
      const minGrossCharge = allMinPrices.length > 0 ? Math.min(...allMinPrices) : 0;
      const maxGrossCharge = allMaxPrices.length > 0 ? Math.max(...allMaxPrices) : 0;

      // Use weighted median (from variation with most hospitals)
      const sortedByHospitals = [...codeData].sort((a, b) => (b.total_hospitals || 0) - (a.total_hospitals || 0));
      const medianGrossCharge = (sortedByHospitals[0] as any)?.[medianField] || 0;

      // Cash price not available in analytics tables
      const avgCashPrice = 0;

      // Calculate price variation across all variations
      const priceVariation = avgGrossCharge > 0
        ? ((maxGrossCharge - minGrossCharge) / avgGrossCharge) * 100
        : 0;

      // Fetch consumer-friendly name and additional details from procedures table FIRST
      // This ensures we never flash a bad description from pricing tables
      const { data: procedureData } = await supabase
        .from("procedures")
        .select("name, description, consumer_friendly_description")
        .eq("code", procedureCode)
        .eq("code_type", config.codeTypeUpper)
        .maybeSingle();

      if (procedureData) {
        setProcedureInfo(procedureData);
      }

      // For description priority: procedures table name > consumer_friendly > pricing table description
      const pricingTableDesc = (nationalAvgData as any)?.[config.descriptionFieldName] || (codeData[0] as any)?.[config.descriptionFieldName];
      const bestDescription = procedureData?.name || pricingTableDesc || "Description not available";

      // Process the data to ensure it matches our interface
      const processedCode: ProcedureCodeData = {
        procedure_code: procedureCode,
        procedure_description: bestDescription,
        consumer_friendly_description: procedureData?.consumer_friendly_description,
        total_hospitals: totalHospitals,
        avg_gross_charge: avgGrossCharge,
        median_gross_charge: medianGrossCharge,
        min_gross_charge: minGrossCharge,
        max_gross_charge: maxGrossCharge,
        avg_discounted_cash: avgCashPrice,
        price_variation: priceVariation
      };
      setSelectedCode(processedCode);

      // Get hospital pricing data from analytics table (aggregated per hospital)
      let hospitalQuery = supabase
        .from("hospital_procedure_analytics")
        .select(`
          hospital_id,
          avg_gross_charge,
          hospital_gross_charge,
          inpatient_outpatient,
          normalized_unit,
          normalized_price_per_unit,
          hospitals!fk_hospital_procedure_analytics_hospital(name, city, state, latitude, longitude)
        `)
        .eq('procedure_code', procedureCode)
        .eq('procedure_type', config.procedureType)
        .not("hospital_gross_charge", "is", null)
        .gt('hospital_gross_charge', 0);

      // Apply setting filter if not "all"
      if (settingFilter !== "all") {
        if (settingFilter === "both") {
          hospitalQuery = hospitalQuery.or("inpatient_outpatient.eq.both,inpatient_outpatient.eq.inpatient and outpatient");
        } else {
          hospitalQuery = hospitalQuery.eq("inpatient_outpatient", settingFilter);
        }
      }

      const { data: hospitalData, error: hospitalError} = await hospitalQuery
        .order("hospital_gross_charge", { ascending: false });

      if (hospitalError) {
        console.error("Error fetching hospital pricing data:", hospitalError);
        throw hospitalError;
      }

      // Process the data from analytics table (normalization already applied)
      const rawProcessedData = hospitalData?.map(item => {
        return {
          hospital_id: item.hospital_id,
          hospital_name: item.hospitals?.name || "Unknown Hospital",
          gross_charge: item.hospital_gross_charge || item.avg_gross_charge || 0,
          original_gross_charge: item.hospital_gross_charge || item.avg_gross_charge || 0,
          has_normalization: false, // Already normalized in analytics table
          normalization_factor: undefined,
          discounted_cash_price: 0, // Not available in analytics table
          min_negotiated_rate: 0, // Not available in analytics table
          max_negotiated_rate: 0, // Not available in analytics table
          inpatient_outpatient: normalizeSetting(item.inpatient_outpatient),
          city: item.hospitals?.city || "Unknown",
          state: item.hospitals?.state || "Unknown",
          latitude: item.hospitals?.latitude,
          longitude: item.hospitals?.longitude,
          drug_unit_quantity: undefined,
          drug_unit_type: undefined,
          normalized_unit: normalizeUnit(item.normalized_unit),
          unit_quantity: item.normalized_price_per_unit, // Use price per unit as the differentiator
          procedure_description: undefined
        };
      }) || [];


      // Deduplicate exact duplicates only (same hospital + setting + price)
      // Keep rows with different prices — they represent legitimate variations
      const seen = new Set<string>();
      const deduped: typeof rawProcessedData = [];
      for (const row of rawProcessedData) {
        const key = `${row.hospital_id}_${row.inpatient_outpatient}_${row.gross_charge}`;
        if (!seen.has(key)) {
          seen.add(key);
          deduped.push(row);
        }
      }
      const processedHospitalData = deduped
        .sort((a, b) => (b.gross_charge || 0) - (a.gross_charge || 0));

      // Create setting groups from cpt_pricing_averages data (matches procedures page)
      // Group by setting_type, normalized_unit, AND unit_quantity to avoid duplicate groups
      const hospitalsBySetting = new Map<string, HospitalPricing[]>();

      processedHospitalData.forEach(hospital => {
        const settingType = normalizeSetting(hospital.inpatient_outpatient);
        const dosageUnit = normalizeUnit(hospital.normalized_unit);
        // Group by setting + dosage only (NOT by price/quantity)
        const settingKey = `${settingType}_${dosageUnit}`;

        if (!hospitalsBySetting.has(settingKey)) {
          hospitalsBySetting.set(settingKey, []);
        }
        hospitalsBySetting.get(settingKey)!.push(hospital);
      });

      // Create setting group objects (primary view - matches procedures page)
      const settingGroupsData: DosageGroup[] = Array.from(hospitalsBySetting.entries()).map(([settingKey, hospitals]) => {
        const prices = hospitals.map(h => h.gross_charge).filter(Boolean);
        const sortedPrices = [...prices].sort((a, b) => a - b);

        const min_price = Math.min(...prices);
        const max_price = Math.max(...prices);
        const avg_price = prices.reduce((sum, price) => sum + price, 0) / prices.length;
        const median_price = sortedPrices.length > 0
          ? sortedPrices[Math.floor(sortedPrices.length / 2)]
          : 0;

        // Calculate average price per unit (if available)
        const pricesPerUnit = hospitals.map(h => h.unit_quantity).filter(Boolean);
        const avg_price_per_unit = pricesPerUnit.length > 0
          ? pricesPerUnit.reduce((sum, price) => sum + price, 0) / pricesPerUnit.length
          : undefined;

        // Extract setting type and dosage unit from the key
        const parts = settingKey.split('_');
        const settingType = parts[0];
        const dosageUnit = parts.slice(1).join('_'); // Everything after the first underscore

        return {
          dosage_quantity: 1,
          dosage_unit: dosageUnit, // Actual dosage unit (mcg, ml, etc)
          setting_type: settingType, // Setting type (inpatient, outpatient, etc)
          unit_quantity: avg_price_per_unit, // Average price per unit for this group
          hospital_count: hospitals.length,
          min_price,
          max_price,
          avg_price,
          median_price,
          price_per_unit: avg_price_per_unit || avg_price,
          hospitals: hospitals.sort((a, b) => (b.gross_charge || 0) - (a.gross_charge || 0))
        };
      }).sort((a, b) => {
        // Sort order: inpatient, outpatient, both, unspecified (by setting first, then by hospital count)
        const order = { inpatient: 1, outpatient: 2, both: 3, unspecified: 4 };
        const settingCompare = (order[a.setting_type as keyof typeof order] || 5) - (order[b.setting_type as keyof typeof order] || 5);
        if (settingCompare !== 0) return settingCompare;

        // Within same setting, sort by hospital count descending (most hospitals first)
        return b.hospital_count - a.hospital_count;
      });

      // Create dosage groups (secondary view) - only when there are actual dosage variations
      const hospitalsByDosage = new Map<string, HospitalPricing[]>();
      const hospitalsWithDosage = processedHospitalData.filter(hospital =>
        hospital.normalized_unit && hospital.normalized_unit !== 'unspecified'
      );

      hospitalsWithDosage.forEach(hospital => {
        const dosageKey = hospital.normalized_unit || 'unspecified';
        if (!hospitalsByDosage.has(dosageKey)) {
          hospitalsByDosage.set(dosageKey, []);
        }
        hospitalsByDosage.get(dosageKey)!.push(hospital);
      });

      const dosageGroupsData: DosageGroup[] = Array.from(hospitalsByDosage.entries()).map(([dosageKey, hospitals]) => {
        const prices = hospitals.map(h => h.gross_charge).filter(Boolean);
        const sortedPrices = [...prices].sort((a, b) => a - b);

        const min_price = Math.min(...prices);
        const max_price = Math.max(...prices);
        const avg_price = prices.reduce((sum, price) => sum + price, 0) / prices.length;
        const median_price = sortedPrices.length > 0
          ? sortedPrices[Math.floor(sortedPrices.length / 2)]
          : 0;

        return {
          dosage_quantity: 1,
          dosage_unit: dosageKey,
          hospital_count: hospitals.length,
          min_price,
          max_price,
          avg_price,
          median_price,
          price_per_unit: avg_price,
          hospitals: hospitals.sort((a, b) => (b.gross_charge || 0) - (a.gross_charge || 0))
        };
      }).sort((a, b) => a.dosage_quantity - b.dosage_quantity);

      // Store both types of groups
      setSettingGroups(settingGroupsData); // Primary display (matches procedures page)
      setDosageGroups(dosageGroupsData); // Secondary display (optional)
      setHospitalPricing(processedHospitalData);

      // Always show dosage column for transparency - even if all records have same/no dosage
      // This helps users understand when hospitals don't specify dosage information
      setHasDosageData(true);

      // Default to NOT grouping by dosage (show setting groups instead)
      setGroupByDosage(false);
    } catch (error) {
      console.error(`Error fetching specific ${config.displayName}:`, error);
      toast({
        title: "Error",
        description: `Failed to fetch details for ${config.displayName} ${procedureCode}. Please try again or check if the code exists.`,
        variant: "destructive",
      });
      setSelectedCode(null);
      setHospitalPricing([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCodes = procedureCodes.filter(code =>
    code.procedure_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    code.procedure_description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter hospital pricing by setting
  const filteredHospitalPricing = hospitalPricing.filter(item => {
    if (settingFilter === "all") return true;
    if (settingFilter === "both") {
      return item.inpatient_outpatient?.toLowerCase().includes("both") ||
             item.inpatient_outpatient?.toLowerCase().includes("inpatient and outpatient");
    }
    return item.inpatient_outpatient?.toLowerCase().includes(settingFilter.toLowerCase());
  });

  // Improved outlier detection: use percentile-based approach
  const sortedPrices = filteredHospitalPricing.map(item => item.gross_charge).filter(price => price > 0).sort((a, b) => a - b);
  const q3Index = Math.floor(sortedPrices.length * 0.75);
  const q1Index = Math.floor(sortedPrices.length * 0.25);
  const p95Index = Math.floor(sortedPrices.length * 0.95);
  const p5Index = Math.floor(sortedPrices.length * 0.05);

  const q3 = sortedPrices[q3Index] || 0;
  const q1 = sortedPrices[q1Index] || 0;
  const p95 = sortedPrices[p95Index] || 0;
  const p5 = sortedPrices[p5Index] || 0;

  // Flag as outliers only if they're 20x beyond the 95th percentile or 1/5th below the 5th percentile
  const upperOutlierThreshold = p95 * 20; // 20x the 95th percentile - only truly insane prices
  const lowerOutlierThreshold = p5 * 0.2; // 1/5th of the 5th percentile

  // Calculate actual average from displayed hospitals for accurate markup percentages
  const displayedHospitalsAverage = filteredHospitalPricing.length > 0
    ? filteredHospitalPricing.reduce((sum, item) => sum + (item.gross_charge || 0), 0) / filteredHospitalPricing.length
    : 0;

  // Separate outliers from normal data for chart display
  const normalPricing = filteredHospitalPricing.filter(item =>
    item.gross_charge <= upperOutlierThreshold &&
    item.gross_charge >= lowerOutlierThreshold
  );
  const upperOutliers = filteredHospitalPricing.filter(item => item.gross_charge > upperOutlierThreshold);
  const lowerOutliers = filteredHospitalPricing.filter(item => item.gross_charge < lowerOutlierThreshold);
  const outlierPricing = [...upperOutliers, ...lowerOutliers];

  // Use normal pricing for chart (excludes outliers for readability)
  const chartData = normalPricing.slice(0, 50).map(item => ({
    hospital: item.hospital_name.substring(0, 20) + (item.has_normalization ? " ⚡" : "") + "...",
    gross_charge: item.gross_charge,
    original_charge: item.original_gross_charge,
    discounted_cash: item.discounted_cash_price || 0,
    setting: item.inpatient_outpatient || "Unknown",
    has_normalization: item.has_normalization,
    normalization_factor: item.normalization_factor
  }));

  // Memoize map data so it doesn't recreate on every expand/collapse toggle
  const mapHospitals = useMemo(() =>
    hospitalPricing.map((h, idx) => ({
      hospital_id: h.hospital_id,
      hospital_name: h.hospital_name,
      latitude: h.latitude || 0,
      longitude: h.longitude || 0,
      city: h.city,
      state: h.state,
      gross_charge: h.gross_charge,
      inpatient_outpatient: h.inpatient_outpatient,
      rank: idx + 1,
    })),
    [hospitalPricing]
  );

  // Get gradient color based on markup percentage (absolute ranges)
  const getGradientStyle = (markupPercentage: number) => {
    let backgroundColor: string;
    let textColor: string;
    
    if (markupPercentage <= -10) {
      // Very low markup (negative) - deep green gradient
      const intensity = Math.min(1, Math.abs(markupPercentage + 10) / 50); // Scale for -10% to -60%
      backgroundColor = `rgba(34, 197, 94, ${0.3 + intensity * 0.5})`;
      textColor = intensity > 0.4 ? "text-white" : "text-green-900";
    } else if (markupPercentage < 10) {
      // Near-neutral markup (-10% to 10%) - black/gray gradient
      const distanceFromZero = Math.abs(markupPercentage);
      const intensity = distanceFromZero / 10;
      backgroundColor = `rgba(64, 64, 64, ${0.1 + intensity * 0.3})`;
      textColor = "text-foreground";
    } else if (markupPercentage < 50) {
      // Moderate markup (10% to 50%) - light red gradient
      const intensity = (markupPercentage - 10) / 40;
      backgroundColor = `rgba(239, 68, 68, ${0.2 + intensity * 0.3})`;
      textColor = intensity > 0.5 ? "text-white" : "text-red-900";
    } else {
      // High markup (50%+) - deep red gradient
      const intensity = Math.min(1, (markupPercentage - 50) / 100); // Scale for 50% to 150%+
      backgroundColor = `rgba(239, 68, 68, ${0.5 + intensity * 0.4})`;
      textColor = "text-white";
    }
    
    return { backgroundColor, color: textColor };
  };

  // SEO metadata
  const consumerDesc = staticDetail?.consumerName || selectedCode?.consumer_friendly_description;
  const pageTitle = selectedCode
    ? `${config.displayName} ${selectedCode.procedure_code} - ${consumerDesc || selectedCode.procedure_description} | Hospital Price Comparison`
    : `${config.pluralName} - Medical Codes | Hospital Price Transparency`;

  const pageDescription = selectedCode
    ? `Compare hospital prices for ${config.displayName} ${selectedCode.procedure_code} (${consumerDesc || selectedCode.procedure_description}). Average price: $${Math.round(selectedCode.avg_gross_charge || 0).toLocaleString()}, reported by ${selectedCode.total_hospitals?.toLocaleString()} hospitals.`
    : `Search and compare hospital prices for ${config.pluralName}. Find the best prices for medical procedures across hospitals.`;

  // Canonical URL path per code type. config.codeType is a data key, not a
  // route: 'drug' has no /drug route (real routes: /drugs, /ndc) so its old
  // canonical 404'd; 'revenue' canonicalizes to /rev to match the bot-SSR
  // canonical (functions/lib/pages/procedure.ts) and the sitemap.
  const canonicalPath =
    config.codeType === 'drug' ? 'drugs'
    : config.codeType === 'revenue' ? 'rev'
    : config.codeType;
  const canonicalUrl = selectedCode
    ? `${brand.primaryDomain}/${canonicalPath}/${selectedCode.procedure_code}`
    : `${brand.primaryDomain}/${canonicalPath}`;

  return (
    <TooltipProvider>
      {!embedded && (
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`${config.pluralName}, medical billing codes, hospital prices, ${selectedCode ? `${config.codeTypeUpper} ${selectedCode.procedure_code}, ${selectedCode.consumer_friendly_description || selectedCode.procedure_description}` : 'medical procedures, healthcare costs'}`} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={brand.social.twitter} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={brand.social.ogImage} />
        {selectedCode && (
          <>
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@graph": [
                  {
                    "@type": "MedicalCode",
                    "name": `${config.displayName} ${selectedCode.procedure_code}`,
                    "description": selectedCode.consumer_friendly_description || selectedCode.procedure_description,
                    "codeValue": selectedCode.procedure_code,
                    "codingSystem": config.codeTypeUpper,
                    "url": canonicalUrl,
                    "medicalSpecialty": "General Medicine",
                    "associatedAnatomy": selectedCode.procedure_description,
                  },
                  {
                    "@type": "FAQPage",
                    "mainEntity": [
                      {
                        "@type": "Question",
                        "name": `How much does ${config.displayName} ${selectedCode.procedure_code} cost?`,
                        "acceptedAnswer": { "@type": "Answer", "text": `The estimated national average hospital price for ${config.displayName} ${selectedCode.procedure_code} (${consumerDesc || selectedCode.procedure_description}) is approximately $${Math.round(selectedCode.avg_gross_charge || 0).toLocaleString()}${selectedCode.median_gross_charge ? `, with an estimated median price of approximately $${Math.round(selectedCode.median_gross_charge).toLocaleString()}` : ''}. These estimates are based on chargemaster data reported by ${selectedCode.total_hospitals?.toLocaleString()} hospitals across the United States.${selectedCode.min_gross_charge ? ` Listed charges range from approximately $${Math.round(selectedCode.min_gross_charge).toLocaleString()} to $${Math.round(selectedCode.max_gross_charge || 0).toLocaleString()}.` : ''} Actual out-of-pocket costs may vary based on insurance, negotiated rates, and individual circumstances.` },
                      },
                      ...(staticDetail?.llmSummary ? [{
                        "@type": "Question" as const,
                        "name": `What is ${config.displayName} ${selectedCode.procedure_code} (${consumerDesc || selectedCode.procedure_description})?`,
                        "acceptedAnswer": { "@type": "Answer", "text": staticDetail.llmSummary.replace(/<[^>]*>/g, '').slice(0, 1500) },
                      }] : []),
                      ...(staticDetail?.educationalHtml ? [{
                        "@type": "Question" as const,
                        "name": `What should patients know about ${consumerDesc || selectedCode.procedure_description}?`,
                        "acceptedAnswer": { "@type": "Answer", "text": staticDetail.educationalHtml.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().slice(0, 1500) },
                      }] : []),
                    ],
                  },
                  // DefinedTerm — helps Google understand this as a formal medical code
                  {
                    "@type": "DefinedTerm",
                    "name": `${config.displayName} ${selectedCode.procedure_code}`,
                    "description": consumerDesc || selectedCode.procedure_description,
                    "termCode": selectedCode.procedure_code,
                    "inDefinedTermSet": {
                      "@type": "DefinedTermSet",
                      "name": config.codeTypeUpper === 'CPT' ? 'Current Procedural Terminology (CPT)' : config.codeTypeUpper === 'HCPCS' ? 'Healthcare Common Procedure Coding System (HCPCS)' : config.codeTypeUpper === 'DRG' ? 'Diagnosis Related Groups (MS-DRG)' : config.codeTypeUpper === 'REV' ? 'Revenue Codes (UB-04)' : config.codeTypeUpper === 'NDC' ? 'National Drug Code (NDC)' : config.codeTypeUpper,
                    },
                  },
                  // MedicalWebPage with Speakable — voice search optimization
                  {
                    "@type": "MedicalWebPage",
                    "name": `${config.displayName} ${selectedCode.procedure_code} — ${consumerDesc || selectedCode.procedure_description}`,
                    "about": { "@type": "MedicalCode", "codeValue": selectedCode.procedure_code, "codingSystem": config.codeTypeUpper },
                    "url": canonicalUrl,
                    "lastReviewed": new Date().toISOString().split('T')[0],
                    "medicalAudience": { "@type": "MedicalAudience", "audienceType": "Patient" },
                    "speakable": {
                      "@type": "SpeakableSpecification",
                      "cssSelector": [".text-3xl", ".font-bold.text-lg", ".text-2xl.font-bold"],
                    },
                  },
                ],
              })}
            </script>
          </>
        )}
      </Helmet>
      )}
      <div className={embedded ? "p-3" : "min-h-screen bg-background p-6 pb-24"}>
        <div className="mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-4">
            {code && !isEmbedded && !embedded && (
              <Button
                variant="outline"
                size="icon"
                onClick={goBack}
                className="mt-1"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div>
              <h1 className="text-3xl font-bold" style={isAhp ? { ...headingFont, color: heroAccentColor } : {}}>{code ? `${config.displayName} ${code}` : t(`codes:${i18nCodeType}.title`)}</h1>
              <p className="text-muted-foreground mt-2" style={bodyFont}>
                {t(`codes:${i18nCodeType}.subtitle`)}
              </p>
            </div>
          </div>
          <FileText className="w-12 h-12" style={isAhp ? { color: heroAccentColor } : undefined} />
        </div>

        {/* Search and Filters - Only show when not viewing specific code */}
        {!code && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      placeholder={t(`codes:${i18nCodeType}.searchPlaceholder`)}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <Button onClick={fetchProcedureCodes} disabled={isLoading}>
                    <Search className="w-4 h-4 mr-2" />
                    {t('codes:shared.search')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {code && !selectedCode && (staticDetail !== null || isLoading) ? (
          // Code specified but not resolved yet. We're "resolving" while the
          // static R2 query is still in flight (staticDetail === undefined) OR
          // the Supabase fallback is running (isLoading). Only once BOTH have
          // finished without a hit do we fall through to the not-found state —
          // so we never flash "no data" while the data is still loading.
          <Card>
            <CardContent className="py-12">
              <div className="flex flex-col items-center justify-center gap-3 text-center text-muted-foreground">
                <Loader2 className="w-7 h-7 animate-spin" style={isAhp ? { color: heroAccentColor } : undefined} />
                <span>{t('codes:shared.loadingCodeData', { codeType: config.displayName, code, defaultValue: `Loading ${config.displayName} ${code}…` })}</span>
              </div>
            </CardContent>
          </Card>
        ) : selectedCode ? (
          // Detailed Code View
          <div className="space-y-6">
            <Card className={isAhp ? 'border-t-4 border-t-[#002868]' : ''}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <CardTitle className="flex items-center gap-2" style={headingFont}>
                    <FileText className="w-5 h-5" style={isAhp ? { color: heroAccentColor } : undefined} />
                    {config.displayName} {selectedCode.procedure_code}
                  </CardTitle>
                  {/* Visible share button — opens the same modal as right-click. Per-code
                      context is preset (codeType + code + label) so the modal templates
                      generate code-aware social copy ("CPT 99213 costs $50–$50,000…"). */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShareOpen(true)}
                    className="flex items-center gap-2"
                    title="Share this code (or right-click anywhere on the page)"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    Share
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">{t('codes:shared.description')}</p>
                    {selectedCode.consumer_friendly_description ? (
                      <div className="space-y-1">
                        <p className="font-bold text-lg" style={isAhp ? { color: heroAccentColor } : undefined}>{selectedCode.consumer_friendly_description}</p>
                        <p className="text-sm text-muted-foreground italic">"{selectedCode.procedure_description}"</p>
                      </div>
                    ) : (
                      <p className="font-semibold">"{selectedCode.procedure_description}"</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">{t('codes:shared.hospitalsReporting')}</p>
                    <p className="text-2xl font-bold">{selectedCode.total_hospitals}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">{t('codes:shared.averagePrice')}</p>
                    <p className="text-2xl font-bold" style={isAhp ? { color: heroAccentColor } : undefined}>${Math.round(selectedCode.avg_gross_charge || 0).toLocaleString()}</p>
                  </div>
                  {selectedCode.median_gross_charge != null && selectedCode.median_gross_charge > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">{t('codes:shared.medianPrice')}</p>
                      <p className={`text-2xl font-bold ${isAhp ? 'text-[#5a4a2a]' : 'text-muted-foreground'}`}>${Math.round(selectedCode.median_gross_charge).toLocaleString()}</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">{t('codes:shared.priceVariation')}</p>
                    <Badge variant={selectedCode.price_variation > 100 ? "destructive" : "secondary"}>
                      {selectedCode.price_variation > 10000 ? ">10,000%" : `${selectedCode.price_variation?.toFixed(1)}%`}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dignity's Guide — moved to top so Luigi explains the code first */}
            {selectedCode && (
              <DignityEducationTab
                title={`${config.displayName} ${selectedCode.procedure_code}`}
                educationalHtml={educationalHtml || undefined}
                llmSummary={staticDetail?.llmSummary || undefined}
                procedureCode={selectedCode.procedure_code}
                codeType={config.codeTypeUpper}
                relatedScenarios={relatedScenariosSimple || undefined}
              />
            )}

            {/* Related Scenarios — full cards with phase breakdown + severity bar */}
            {relatedScenarios && relatedScenarios.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold" style={headingFont}>
                  Care Pathways Using {config.displayName} {selectedCode?.procedure_code}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {relatedScenarios.map(scenario => (
                    <ScenarioCard
                      key={scenario.id}
                      scenario={scenario}
                      isStarred={false}
                      theme={cardTheme}
                      compact={false}
                      onToggleBookmark={() => {}}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Educational context for REV/DRG codes */}
            {CODE_TYPE_EDUCATION[config.codeType] && (
              <Card className={isAhp ? 'border-l-4 border-l-[#C5A55A] bg-[#002868]/5' : 'border-l-4 border-l-blue-400 bg-blue-50/50 dark:bg-blue-950/20'}>
                <CardContent className="py-4">
                  <div className="flex gap-3">
                    <Info className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isAhp ? 'text-[#C5A55A]' : 'text-blue-500'}`} />
                    <div className="space-y-2">
                      <p className="font-semibold text-sm">{CODE_TYPE_EDUCATION[config.codeType].title}</p>
                      <p className="text-sm text-muted-foreground">{CODE_TYPE_EDUCATION[config.codeType].explanation}</p>
                      <p className="text-sm text-muted-foreground">{CODE_TYPE_EDUCATION[config.codeType].priceNote}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* International Price Comparison */}
            {/* Hospital Pricing Map */}
            {selectedCode && mapHospitals.length > 0 && (
              <HospitalPricingMap
                hospitals={mapHospitals}
                procedureCode={selectedCode.procedure_code}
                codeType={config.codeTypeUpper}
                avgPrice={selectedCode.avg_gross_charge}
              />
            )}

            {/* Price Visualization Chart */}
            <Card>
              <CardHeader>
                <CardTitle style={headingFont}>{isAhp ? t('codes:shared.hospitalPriceComparison') : t('codes:shared.hospitalPriceGougingComparison')}</CardTitle>
                <p className="text-sm text-muted-foreground mt-2">
                  {t('codes:shared.chartRedBlueDescription')}
                  {outlierPricing.length > 0 && (
                    <span className="text-orange-600 font-medium">
                      {" "}{t('codes:shared.chartExcludesOutliers', { count: outlierPricing.length })}
                    </span>
                  )}
                </p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  {/* AHP patriotic palette: AHP red (#B22234) for standard pricing bars,
                      navy (#002868) for unit-normalized bars, AHP gold (#C5A55A) for the
                      reference avg line. Cream/navy text on the axes & tooltip to match
                      the StatCard / Quality slider aesthetic everywhere else on the page. */}
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(245,235,215,0.10)' : 'rgba(0,18,56,0.08)'} />
                    <XAxis
                      dataKey="hospital"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      fontSize={12}
                      stroke={isDark ? 'rgba(245,235,215,0.55)' : 'rgba(0,18,56,0.55)'}
                      tick={{ fill: isDark ? 'rgba(245,235,215,0.65)' : 'rgba(0,18,56,0.65)' }}
                    />
                    <YAxis
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      stroke={isDark ? 'rgba(245,235,215,0.55)' : 'rgba(0,18,56,0.55)'}
                      tick={{ fill: isDark ? 'rgba(245,235,215,0.65)' : 'rgba(0,18,56,0.65)' }}
                    />
                    <Tooltip
                      formatter={(value, name, props) => {
                        const data = props.payload;
                        if (data.has_normalization) {
                          return [
                            `$${Number(value).toLocaleString()} (normalized from $${Number(data.original_charge).toLocaleString()})`,
                            t('codes:shared.hospitalCharge')
                          ];
                        }
                        return [
                          `$${Number(value).toLocaleString()}`,
                          t('codes:shared.hospitalCharge')
                        ];
                      }}
                      labelFormatter={(label) => label.replace(' ⚡...', '').replace('...', '') + (label.includes('⚡') ? ` (${t('codes:shared.unitNormalized')})` : '')}
                      contentStyle={{
                        backgroundColor: isDark ? 'rgba(2, 22, 60, 0.95)' : '#FBF7EE',
                        border: `1px solid ${isDark ? 'rgba(197, 165, 90, 0.45)' : 'rgba(0, 18, 56, 0.20)'}`,
                        borderRadius: 6,
                        color: isDark ? '#F5EBD7' : '#001238',
                      }}
                      labelStyle={{ color: isDark ? '#F5EBD7' : '#001238', fontWeight: 600 }}
                      itemStyle={{ color: isDark ? '#F5EBD7' : '#001238' }}
                    />
                    <Bar
                      dataKey="gross_charge"
                      name={t('codes:shared.hospitalCharge')}
                      radius={[4, 4, 0, 0]}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          // AHP red for standard sticker price, navy for unit-normalized so the
                          // viewer can tell at a glance which bars represent comparable pricing.
                          fill={entry.has_normalization ? '#002868' : '#B22234'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {/* Legend chips that match the patriotic palette */}
                <div className="flex flex-wrap gap-3 mt-3 text-[11px]" style={{ color: isDark ? 'rgba(245,235,215,0.65)' : 'rgba(0,18,56,0.65)' }}>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm" style={{ background: '#B22234' }} />
                    Standard chargemaster price
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm" style={{ background: '#002868' }} />
                    Unit-normalized (per dose vs per vial)
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Setting Filter - Care Settings */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{t('codes:shared.careSetting')}:</span>
                    <div className="flex gap-2">
                      {[
                        { value: "all", label: t('codes:shared.allSettings') },
                        { value: "inpatient", label: t('codes:shared.inpatient') },
                        { value: "outpatient", label: t('codes:shared.outpatient') },
                        { value: "both", label: t('codes:shared.both') }
                      ].map((option) => (
                        <Button
                          key={option.value}
                          size="sm"
                          variant={settingFilter === option.value ? "default" : "outline"}
                          onClick={() => setSettingFilter(option.value)}
                          className="text-xs"
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Setting/Dosage Grouping Toggle - Only show when we have both types of groups */}
                  {hasDosageData && dosageGroups.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{t('codes:shared.view')}:</span>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={!groupByDosage ? "default" : "outline"}
                          onClick={() => setGroupByDosage(false)}
                          className="text-xs"
                        >
                          {t('codes:shared.groupBySetting', { count: settingGroups.length })}
                        </Button>
                        <Button
                          size="sm"
                          variant={groupByDosage ? "default" : "outline"}
                          onClick={() => setGroupByDosage(true)}
                          className="text-xs"
                        >
                          {t('codes:shared.groupByDosage', { count: dosageGroups.length })}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Pricing Buckets — patriotic StatCard layout grouped by dosage unit, then setting.
                Mirrors the hospital/uuid Quality + StatCard aesthetic (cream/navy gradient cards,
                AHP-gold flag-stripe accent, big tabular-nums readouts).
                Replaces the previously-flat "12 rows of buckets" table with a visual hierarchy that
                surfaces the most-populous cohorts first and de-emphasizes niche unit configurations. */}
            {settingGroups.length > 0 && (() => {
              // ── Group settingGroups by dosage_unit, then sort by hospital count ─────────
              type SettingBucket = DosageGroup & { _key: string };
              type UnitGroup = {
                unitKey: string;        // 'unspecified' | 'IU' | 'ml' | 'each' | etc.
                unitLabel: string;      // Display label
                buckets: SettingBucket[];
                /** Unique hospital count for the unit group. Prefer the all_settings bucket
                 *  (which IS the union of inpatient + outpatient + unspecified at the source).
                 *  Fall back to MAX of per-setting counts since hospitals frequently report
                 *  the same code under multiple settings — naive summation double-counts. */
                uniqueHospitals: number;
              };
              const byUnit = new Map<string, UnitGroup>();
              settingGroups.forEach((g) => {
                const unitKey = (!g.dosage_unit || g.dosage_unit === 'unspecified' || g.dosage_unit === 'other')
                  ? '__no_unit__'
                  : g.dosage_unit;
                const ug = byUnit.get(unitKey) || {
                  unitKey,
                  unitLabel: unitKey === '__no_unit__'
                    ? 'Standard pricing (no unit dimension)'
                    : `Per ${formatUnitDisplay(unitKey)}`,
                  buckets: [],
                  uniqueHospitals: 0,
                };
                ug.buckets.push({ ...g, _key: `${g.setting_type || 'unspecified'}_${g.dosage_unit || 'unspecified'}` });
                byUnit.set(unitKey, ug);
              });
              // Compute uniqueHospitals per unit group from the all_settings bucket if present.
              byUnit.forEach((ug) => {
                const allSettings = ug.buckets.find(b => b.setting_type === 'all_settings');
                ug.uniqueHospitals = allSettings
                  ? allSettings.hospital_count
                  // No all_settings cohort — best estimate is the max bucket. Hospitals reporting
                  // both inpatient and outpatient still count once via the larger bucket.
                  : Math.max(...ug.buckets.map(b => b.hospital_count));
              });
              // Sort buckets within each unit by hospital count desc; preserve "all_settings" first if present
              byUnit.forEach((ug) => {
                ug.buckets.sort((a, b) => {
                  const aAll = a.setting_type === 'all_settings' ? -1 : 0;
                  const bAll = b.setting_type === 'all_settings' ? -1 : 0;
                  if (aAll !== bAll) return aAll - bAll;
                  return b.hospital_count - a.hospital_count;
                });
              });
              // Sort unit groups: '__no_unit__' first, then by unique hospital count desc
              const unitGroups = Array.from(byUnit.values()).sort((a, b) => {
                if (a.unitKey === '__no_unit__') return -1;
                if (b.unitKey === '__no_unit__') return 1;
                return b.uniqueHospitals - a.uniqueHospitals;
              });

              // ── Visual tokens (mirror HospitalProfile.tsx StatCard) ────────────────
              const t_ = isAhp && isDark ? {
                cardBg: 'linear-gradient(180deg, rgba(2, 22, 60, 0.92) 0%, rgba(0, 14, 44, 0.96) 100%)',
                cardBorder: 'rgba(197, 165, 90, 0.18)',
                cardShadow: '0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 20px -14px rgba(0,0,0,0.6)',
                groupHeader: '#F5EBD7',
                nameText: '#F5EBD7',
                mutedText: 'rgba(245, 235, 215, 0.65)',
                fadedText: 'rgba(245, 235, 215, 0.45)',
                divider: 'rgba(197, 165, 90, 0.18)',
                pillTopBg: 'rgba(197, 165, 90, 0.20)',
                pillTopBd: 'rgba(197, 165, 90, 0.65)',
                pillTopTx: '#F5EBD7',
              } : {
                cardBg: 'linear-gradient(180deg, #FFFFFF 0%, #FBF7EE 100%)',
                cardBorder: 'rgba(0, 18, 56, 0.12)',
                cardShadow: '0 1px 0 rgba(255,255,255,0.6) inset, 0 4px 14px -6px rgba(0, 18, 56, 0.10)',
                groupHeader: '#001238',
                nameText: '#001238',
                mutedText: 'rgba(0, 18, 56, 0.65)',
                fadedText: 'rgba(0, 18, 56, 0.55)',
                divider: 'rgba(197, 165, 90, 0.30)',
                pillTopBg: 'rgba(197, 165, 90, 0.18)',
                pillTopBd: 'rgba(197, 165, 90, 0.65)',
                pillTopTx: '#7A5F1F',
              };
              const formatSettingLabel = (s?: string) => {
                if (s === 'all_settings') return 'All settings combined';
                if (s === 'inpatient') return 'Inpatient';
                if (s === 'outpatient') return 'Outpatient';
                if (s === 'both') return 'Inpatient + Outpatient';
                return 'Setting unspecified';
              };
              // Tone per bucket — most-populous in the unit group = 'good' (gold), niche = 'na'
              const toneFor = (b: SettingBucket, ug: UnitGroup): 'good' | 'neutral' | 'bad' | 'na' => {
                if (b.setting_type === 'all_settings') return 'good';
                const max = Math.max(...ug.buckets.map(x => x.hospital_count));
                if (b.hospital_count >= max * 0.75) return 'neutral';
                if (b.hospital_count >= max * 0.25) return 'na';
                return 'na';
              };
              const toneStyles = isAhp && isDark ? {
                good:    { stripe: '#C5A55A', fill: '#F5EBD7', soft: 'rgba(197, 165, 90, 0.10)' },
                neutral: { stripe: '#9A7B33', fill: '#C5A55A', soft: 'rgba(197, 165, 90, 0.06)' },
                bad:     { stripe: '#B22234', fill: '#FCA5A5', soft: 'rgba(178, 34, 52, 0.10)' },
                na:      { stripe: '#7B8AA0', fill: '#D4C9AE', soft: 'rgba(245, 235, 215, 0.04)' },
              } : {
                good:    { stripe: '#C5A55A', fill: '#001238', soft: 'rgba(197, 165, 90, 0.10)' },
                neutral: { stripe: '#9A7B33', fill: '#7A5F1F', soft: 'rgba(197, 165, 90, 0.06)' },
                bad:     { stripe: '#B22234', fill: '#8B1828', soft: 'rgba(178, 34, 52, 0.06)' },
                na:      { stripe: '#94A3B8', fill: '#475569', soft: 'rgba(0, 18, 56, 0.03)' },
              };

              return (
                <Card className="mb-6" id="variations">
                  <CardHeader>
                    <CardTitle style={headingFont}>Pricing breakdown</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Buckets grouped by dosage unit, then by care setting. Sorted so the most-reported
                      cohorts surface first. Click any card to expand the full hospital list for that bucket.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    {unitGroups.map((ug, ugi) => (
                      <div key={ug.unitKey}>
                        <div className="flex items-baseline gap-2 mb-2">
                          <span className="text-[10px] uppercase tracking-[0.10em] font-bold opacity-50">📦 Group {ugi + 1}</span>
                          <h4 className="text-base font-semibold" style={{ color: t_.groupHeader, fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 }}>
                            {ug.unitLabel}
                          </h4>
                          <span className="text-[11px] tabular-nums" style={{ color: t_.fadedText }}>
                            · {ug.uniqueHospitals.toLocaleString()} {ug.uniqueHospitals === 1 ? 'hospital' : 'hospitals'} reporting · {ug.buckets.length} {ug.buckets.length === 1 ? 'setting variation' : 'setting variations'}
                          </span>
                          {ugi === 0 && ug.unitKey === '__no_unit__' && (
                            <span
                              className="text-[9px] uppercase tracking-[0.08em] font-bold px-2 py-[2px] rounded-sm border whitespace-nowrap"
                              style={{ background: t_.pillTopBg, borderColor: t_.pillTopBd, color: t_.pillTopTx }}
                            >
                              Most common
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {ug.buckets.map((b) => {
                            const tone = toneFor(b, ug);
                            const ts = toneStyles[tone];
                            const isExpanded = expandedGroups.has(b._key);
                            const priceVar = b.avg_price > 0
                              ? ((b.max_price - b.min_price) / b.avg_price) * 100
                              : 0;
                            const HOSP_PAGE = 25;
                            const curPage = groupPages[b._key] || 0;
                            // Per-bucket chart filter (state or system pulled from chart click).
                            // Lives in groupPages map under a synthetic key so each card has its own.
                            const filterKey = `${b._key}__filter`;
                            const filterState = (groupPages as any)[filterKey] as { type: 'state' | 'system'; value: string } | undefined;
                            // Dedupe hospitals client-side (see commit f0f87ebb for rationale).
                            const dedupedHosps = (() => {
                              const byId = new Map<string, HospitalPricing>();
                              const byShape = new Map<string, HospitalPricing>();
                              const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
                              for (const h of b.hospitals) {
                                if (h.hospital_id && byId.has(h.hospital_id)) {
                                  const existing = byId.get(h.hospital_id)!;
                                  if ((h.gross_charge ?? 0) > (existing.gross_charge ?? 0)) byId.set(h.hospital_id, h);
                                  continue;
                                }
                                if (h.hospital_id) byId.set(h.hospital_id, h);
                                const shapeKey = `${norm(h.hospital_name)}|${norm(h.city)}|${(h.state || '').toUpperCase()}|${h.gross_charge ?? 0}`;
                                if (byShape.has(shapeKey)) {
                                  if (h.hospital_id) byId.delete(h.hospital_id);
                                  continue;
                                }
                                byShape.set(shapeKey, h);
                              }
                              return Array.from(byId.values()).sort((a, c) => (c.gross_charge ?? 0) - (a.gross_charge ?? 0));
                            })();

                            // Apply filter to dedupedHosps before pagination.
                            // Filter values are emitted by BucketCharts via setBucketFilter.
                            const filteredHosps = filterState
                              ? dedupedHosps.filter(h => {
                                  if (filterState.type === 'state') return (h.state || '??').toUpperCase() === filterState.value;
                                  if (filterState.type === 'system') {
                                    const sys = financeMap?.get(h.hospital_id)?.system || '__none__';
                                    return sys === filterState.value;
                                  }
                                  return true;
                                })
                              : dedupedHosps;
                            const totalUnique = filteredHosps.length;
                            const totalPages = Math.max(1, Math.ceil(totalUnique / HOSP_PAGE));
                            const start = curPage * HOSP_PAGE;
                            const visibleHosps = filteredHosps.slice(start, start + HOSP_PAGE);
                            const setBucketFilter = (f: { type: 'state' | 'system'; value: string } | undefined) => {
                              setGroupPages(prev => ({
                                ...prev,
                                [b._key]: 0,
                                [filterKey]: f as any,
                              }));
                            };
                            return (
                              <div
                                key={b._key}
                                className="relative rounded-md p-4 pl-5 transition-shadow hover:shadow-[0_0_0_1px_rgba(197,165,90,0.45),0_8px_24px_-12px_rgba(0,18,56,0.18)]"
                                style={{
                                  background: `${t_.cardBg}, ${ts.soft}`,
                                  backgroundBlendMode: 'normal, multiply',
                                  border: `1px solid ${t_.cardBorder}`,
                                  boxShadow: t_.cardShadow,
                                  // When expanded, span all columns of the parent grid so the hospital
                                  // table can use full width.
                                  gridColumn: isExpanded ? '1 / -1' : undefined,
                                }}
                              >
                                {/* Flag-stripe left accent */}
                                <div
                                  className="absolute top-2 bottom-2 left-0 w-[3px] rounded-full"
                                  style={{ background: ts.stripe, boxShadow: `0 0 8px ${ts.stripe}55` }}
                                  aria-hidden
                                />

                                {/* Click-target: only the summary half toggles expansion */}
                                <div
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => toggleGroupExpansion(b._key)}
                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroupExpansion(b._key); } }}
                                  className="cursor-pointer"
                                >
                                  {/* Header: setting label + expand chevron */}
                                  <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="text-[12px] font-semibold uppercase tracking-[0.06em]" style={{ color: t_.nameText }}>
                                      {formatSettingLabel(b.setting_type)}
                                    </div>
                                    {isExpanded ? <ChevronDown className="w-4 h-4 opacity-60" /> : <ChevronRight className="w-4 h-4 opacity-60" />}
                                  </div>

                                  {/* Big number: hospital count */}
                                  <div className="flex items-baseline gap-1.5 mb-2">
                                    <span className="font-mono text-[28px] md:text-[34px] font-extrabold tabular-nums leading-none" style={{ color: ts.fill, letterSpacing: '-0.02em' }}>
                                      {b.hospital_count.toLocaleString()}
                                    </span>
                                    <span className="text-[11px]" style={{ color: t_.mutedText }}>
                                      {b.hospital_count === 1 ? 'hospital' : 'hospitals'}
                                    </span>
                                  </div>

                                  {/* Stats row */}
                                  <div className="grid grid-cols-2 gap-2 pt-2.5" style={{ borderTop: `1px solid ${t_.divider}` }}>
                                    <div>
                                      <div className="text-[9px] uppercase tracking-wider opacity-60">avg</div>
                                      <div className="font-mono text-sm font-semibold tabular-nums" style={{ color: t_.nameText }}>
                                        ${b.avg_price.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-[9px] uppercase tracking-wider opacity-60">median</div>
                                      <div className="font-mono text-sm font-semibold tabular-nums" style={{ color: t_.nameText }}>
                                        ${b.median_price.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Range + variation */}
                                  <div className="mt-2 text-[11px] tabular-nums" style={{ color: t_.fadedText }}>
                                    ${b.min_price.toLocaleString()} – ${b.max_price.toLocaleString()}
                                    <span className="opacity-70"> · {priceVar.toFixed(0)}% variation</span>
                                  </div>
                                </div>

                                {/* Inline hospital list — only when expanded.
                                    Desktop (lg+): 50/50 split — hospitals on left, interactive
                                    distribution + state charts on right. Click any chart bar
                                    to filter the table to that price bin / state.
                                    Mobile: stacks (table first, then charts). */}
                                {isExpanded && (
                                  <div className="mt-4 pt-4 grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ borderTop: `1px solid ${t_.divider}` }}>
                                  <div>
                                    <div className="flex items-center justify-between mb-2 text-[11px]" style={{ color: t_.mutedText }}>
                                      <span className="font-semibold uppercase tracking-wider">
                                        Hospitals · ranked highest → lowest
                                        {filterState && (
                                          <span className="ml-2 normal-case font-normal" style={{ color: '#C5A55A' }}>
                                            · filtered: {filterState.type === 'priceBin' ? filterState.value : filterState.value}
                                            <button
                                              type="button"
                                              onClick={(e) => { e.stopPropagation(); setBucketFilter(undefined); }}
                                              className="ml-1 underline opacity-80 hover:opacity-100"
                                            >
                                              clear
                                            </button>
                                          </span>
                                        )}
                                        {totalUnique < b.hospitals.length && !filterState && (
                                          <span className="ml-2 normal-case font-normal opacity-70">
                                            (deduped from {b.hospitals.length.toLocaleString()} raw records)
                                          </span>
                                        )}
                                      </span>
                                      {totalUnique > HOSP_PAGE && (
                                        <span className="tabular-nums">
                                          showing {start + 1}–{Math.min(start + HOSP_PAGE, totalUnique)} of {totalUnique.toLocaleString()}
                                        </span>
                                      )}
                                    </div>
                                    <div className="overflow-x-auto -mx-1">
                                      <table className="w-full text-[12px]" style={{ color: t_.nameText }}>
                                        <thead>
                                          <tr style={{ borderBottom: `1px solid ${t_.divider}` }}>
                                            <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-wider text-[9px] opacity-60">#</th>
                                            <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-wider text-[9px] opacity-60">Hospital</th>
                                            <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-wider text-[9px] opacity-60">Location</th>
                                            <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-wider text-[9px] opacity-60">Price</th>
                                            <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-wider text-[9px] opacity-60">vs U.S. avg</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {visibleHosps.map((h, hi) => {
                                            const rank = start + hi + 1;
                                            const markup = selectedCode?.avg_gross_charge && h.gross_charge
                                              ? ((h.gross_charge - selectedCode.avg_gross_charge) / selectedCode.avg_gross_charge) * 100
                                              : 0;
                                            const isHighOutlier = h.gross_charge > upperOutlierThreshold;
                                            const isLowOutlier = h.gross_charge < lowerOutlierThreshold;
                                            const markupColor = isHighOutlier ? '#dc2626'
                                              : markup >= 100 ? '#dc2626'
                                              : markup >= 25 ? '#f59e0b'
                                              : markup >= -25 ? (isAhp && isDark ? '#F5EBD7' : '#475569')
                                              : markup >= -50 ? '#84cc16'
                                              : '#10b981';
                                            return (
                                              <tr
                                                key={`${b._key}-${h.hospital_id}-${hi}`}
                                                className="hover:bg-black/5 transition-colors"
                                                style={{ borderBottom: `1px solid ${t_.divider}` }}
                                              >
                                                <td className="px-2 py-2 font-mono tabular-nums opacity-60">{rank}</td>
                                                <td className="px-2 py-2">
                                                  <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/hospital/${h.hospital_id}`); }}
                                                    className="text-left hover:underline truncate max-w-[260px] block font-medium"
                                                    title={h.hospital_name}
                                                    style={{ color: t_.nameText }}
                                                  >
                                                    {h.hospital_name}
                                                  </button>
                                                </td>
                                                <td className="px-2 py-2 text-[11px]" style={{ color: t_.mutedText }}>
                                                  {h.city}, {h.state}
                                                </td>
                                                <td className="px-2 py-2 text-right font-mono tabular-nums font-semibold">
                                                  ${h.gross_charge?.toLocaleString()}
                                                  {(isHighOutlier || isLowOutlier) && (
                                                    <span className="ml-1 text-[9px] uppercase opacity-70" style={{ color: isHighOutlier ? '#dc2626' : '#10b981' }}>
                                                      extreme outlier
                                                    </span>
                                                  )}
                                                </td>
                                                <td className="px-2 py-2 text-right font-mono tabular-nums" style={{ color: markupColor }}>
                                                  {markup > 0 ? '+' : ''}{markup.toFixed(0)}%
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                    {totalPages > 1 && (
                                      <div className="flex items-center justify-center gap-3 mt-3 text-[11px]" style={{ color: t_.mutedText }}>
                                        <button
                                          type="button"
                                          disabled={curPage === 0}
                                          onClick={(e) => { e.stopPropagation(); setGroupPages(prev => ({ ...prev, [b._key]: curPage - 1 })); }}
                                          className="px-2 py-1 rounded border disabled:opacity-30 hover:bg-black/5"
                                          style={{ borderColor: t_.divider }}
                                        >
                                          ← Previous
                                        </button>
                                        <span className="tabular-nums">page {curPage + 1} of {totalPages}</span>
                                        <button
                                          type="button"
                                          disabled={curPage >= totalPages - 1}
                                          onClick={(e) => { e.stopPropagation(); setGroupPages(prev => ({ ...prev, [b._key]: curPage + 1 })); }}
                                          className="px-2 py-1 rounded border disabled:opacity-30 hover:bg-black/5"
                                          style={{ borderColor: t_.divider }}
                                        >
                                          Next →
                                        </button>
                                      </div>
                                    )}
                                  </div>{/* /left pane */}

                                  {/* Right pane — three interactive views: state choropleth, scatter
                                      cross-referencing price vs revenue-per-bed (with CEO comp toggle),
                                      and top hospital systems. All three drive the same filter state
                                      so clicks in any view filter the left-pane table.
                                      Charts deferred to a memo'd subcomponent so the heavy choropleth
                                      doesn't re-render on every parent re-render. */}
                                  <BucketCharts
                                    bucketKey={b._key}
                                    hospitals={dedupedHosps}
                                    financeMap={financeMap}
                                    filterState={filterState}
                                    setBucketFilter={setBucketFilter}
                                    isAhp={isAhp}
                                    isDark={isDark}
                                    t_={t_}
                                  />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })()}

            <div className="space-y-6" id="variations">

              {/* About Section: LLM Summary + Educational Content */}
              {(staticDetail?.llmSummary || educationalHtml) && selectedCode && (
                <div className="w-full">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2" style={headingFont}>
                        <Info className="w-5 h-5" style={isAhp ? { color: heroAccentColor } : undefined} />
                        {t(`codes:${i18nCodeType}.aboutCode`, { code: selectedCode.procedure_code })}
                      </CardTitle>
                      {selectedCode.consumer_friendly_description && (
                        <p className="text-base font-medium mt-1" style={isAhp ? { color: heroAccentColor } : undefined}>
                          {selectedCode.consumer_friendly_description}
                        </p>
                      )}
                    </CardHeader>
                    <CardContent className="overflow-x-hidden break-words space-y-4">
                      {staticDetail?.llmSummary && (
                        <p className="text-base leading-relaxed text-muted-foreground" style={bodyFont}>
                          {staticDetail.llmSummary}
                        </p>
                      )}
                      {educationalHtml && (
                        <div className={`prose ${isAhp ? 'prose-stone' : 'prose-invert'} max-w-none [&>*]:break-words [&>*]:overflow-wrap-anywhere`}>
                          <EducationalContentRenderer
                            htmlContent={educationalHtml}
                            procedureCode={selectedCode.procedure_code}
                            codeType={config.codeTypeUpper}
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>

            {/* USA vs International Prices — moved to the bottom of the page so it sits as
                supplementary context after the consumer has seen U.S. hospital data + breakdowns.
                Default view shows the U.S. baseline + 5 country rows with prev/next pagination;
                clicking "Show all" expands to detailed cards. */}
            {internationalPrices.length > 0 && selectedCode && (() => {
              const INTL_PER_PAGE = 5;
              const totalIntlPages = Math.max(1, Math.ceil(internationalPrices.length / INTL_PER_PAGE));
              const intlStart = intlPage * INTL_PER_PAGE;
              const visibleIntl = isInternationalExpanded
                ? internationalPrices
                : internationalPrices.slice(intlStart, intlStart + INTL_PER_PAGE);
              return (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2" style={headingFont}>
                        <DollarSign className={`w-5 h-5 ${isAhp ? 'text-[#C5A55A]' : ''}`} />
                        {t('codes:shared.usaVsInternational')}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-2">
                        {t('codes:shared.usaVsInternationalSubtitle')}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsInternationalExpanded(!isInternationalExpanded)}
                      className="flex items-center gap-2"
                    >
                      {isInternationalExpanded ? (
                        <><ChevronDown className="w-4 h-4" />{t('codes:shared.collapse')}</>
                      ) : (
                        <><ChevronRight className="w-4 h-4" />Show all {internationalPrices.length}</>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2 font-semibold">{t('codes:shared.country')}</th>
                          <th className="text-right py-2 px-2 font-semibold">{t('codes:shared.priceUsd')}</th>
                          <th className="text-right py-2 px-2 font-semibold hidden sm:table-cell">{t('codes:shared.vsUsAvg')}</th>
                          <th className="text-left py-2 px-2 font-semibold hidden md:table-cell">{t('codes:shared.source')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* USA baseline always pinned at top of every page */}
                        <tr className={`border-b ${isAhp ? 'bg-[#B22234]/5' : 'bg-red-50 dark:bg-red-950/20'}`}>
                          <td className="py-2 px-2"><span className="mr-1">🇺🇸</span> {t('codes:rev.unitedStates')}</td>
                          <td className={`text-right py-2 px-2 font-bold ${isAhp ? 'text-[#B22234]' : 'text-red-600'}`}>
                            ${Math.round(selectedCode.avg_gross_charge).toLocaleString()}
                          </td>
                          <td className="text-right py-2 px-2 hidden sm:table-cell">
                            <Badge variant="outline" className={isAhp ? "bg-[#B22234]/10 text-[#B22234] border-[#B22234]/30 text-xs" : "bg-red-100 text-red-800 border-red-300 text-xs"}>
                              {t('codes:shared.baseline')}
                            </Badge>
                          </td>
                          <td className="py-2 px-2 text-muted-foreground hidden md:table-cell">{t('codes:shared.hospitalTransparencyData')}</td>
                        </tr>
                        {visibleIntl.map((country) => {
                          const markupX = country.priceUSD > 0 ? (selectedCode.avg_gross_charge / country.priceUSD) : 0;
                          return (
                            <tr key={country.countryCode} className="border-b hover:bg-muted/30 transition-colors">
                              <td className="py-2 px-2">
                                {(() => {
                                  const slug = getCountrySlug(country.countryCode);
                                  const inner = <><span className="mr-1">{country.flag}</span> {country.country}</>;
                                  return slug ? (
                                    <Link to={`/international/${slug}`} className="hover:underline" style={{ color: 'inherit' }} title={`See all ${country.country} pricing`}>
                                      {inner}
                                    </Link>
                                  ) : inner;
                                })()}
                              </td>
                              <td className={`text-right py-2 px-2 font-bold ${isAhp ? 'text-[#2b5e49]' : 'text-green-600 dark:text-green-400'}`}>
                                ${Math.round(country.priceUSD).toLocaleString()}
                              </td>
                              <td className="text-right py-2 px-2 hidden sm:table-cell">
                                {markupX > 1 ? (
                                  <span className={`text-xs font-semibold ${isAhp ? 'text-[#B22234]' : 'text-red-500'}`}>
                                    {t('codes:shared.moreInUs', { multiplier: markupX.toFixed(1) })}
                                  </span>
                                ) : markupX > 0 ? (
                                  <span className="text-xs text-muted-foreground">
                                    {t('codes:shared.lessInUs', { multiplier: (1/markupX).toFixed(1) })}
                                  </span>
                                ) : null}
                              </td>
                              <td className="py-2 px-2 text-muted-foreground hidden md:table-cell">{country.source}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination — only when collapsed AND >5 countries */}
                  {!isInternationalExpanded && totalIntlPages > 1 && (
                    <div className="flex items-center justify-center gap-3 mt-3 text-xs text-muted-foreground">
                      <Button variant="outline" size="sm" disabled={intlPage === 0} onClick={() => setIntlPage(p => Math.max(0, p - 1))}>
                        <ChevronRight className="w-3 h-3 rotate-180 mr-1" /> Previous
                      </Button>
                      <span className="tabular-nums">
                        showing {intlStart + 1}–{Math.min(intlStart + INTL_PER_PAGE, internationalPrices.length)} of {internationalPrices.length} countries
                      </span>
                      <Button variant="outline" size="sm" disabled={intlPage >= totalIntlPages - 1} onClick={() => setIntlPage(p => Math.min(totalIntlPages - 1, p + 1))}>
                        Next <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  )}

                  {/* Expanded detailed cards (visible only when "Show all" clicked) */}
                  {isInternationalExpanded && (
                    <div className="mt-6 space-y-3">
                      {internationalPrices.map((country) => {
                        const markupX = country.priceUSD > 0 ? (selectedCode.avg_gross_charge / country.priceUSD) : 0;
                        return (
                          <div key={`detail-${country.countryCode}`} className="p-4 border rounded-lg border-green-200 bg-green-50/30 dark:border-green-900 dark:bg-green-950/20">
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 items-center">
                              <div className="flex items-center gap-2">
                                <span className="text-2xl">{country.flag}</span>
                                <div>
                                  {(() => {
                                    const slug = getCountrySlug(country.countryCode);
                                    return slug ? (
                                      <Link to={`/international/${slug}`} className="font-bold hover:underline inline-flex items-center gap-1" style={{ color: 'inherit' }}>
                                        {country.country}
                                        <ExternalLink className="w-3 h-3 opacity-50" />
                                      </Link>
                                    ) : <p className="font-bold">{country.country}</p>;
                                  })()}
                                  <p className="text-sm text-muted-foreground">{country.healthcareSystem}</p>
                                </div>
                              </div>
                              <div>
                                <p className="text-sm text-muted-foreground">{t('codes:shared.priceUsd')}</p>
                                <p className={`font-bold text-lg ${isAhp ? 'text-[#2b5e49]' : 'text-green-500'}`}>
                                  ${country.priceUSD.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </p>
                                {country.price > 0 && country.currency !== 'USD' && (
                                  <p className="text-xs text-muted-foreground">
                                    {country.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {country.currency}
                                  </p>
                                )}
                                {markupX > 1 && (
                                  <p className={`text-xs font-semibold ${isAhp ? 'text-[#B22234]' : 'text-red-500'}`}>
                                    {t('codes:shared.usIsMoreExpensive', { multiplier: markupX.toFixed(1) })}
                                  </p>
                                )}
                              </div>
                              <div>
                                <p className="text-sm text-muted-foreground">{t('codes:shared.coverage')}</p>
                                <p className="text-sm">{country.consumerCostNotes || country.coverage || country.healthcareSystem}</p>
                              </div>
                              <div>
                                <p className="text-sm text-muted-foreground">{t('codes:shared.source')}</p>
                                <p className="text-sm">{country.source}</p>
                              </div>
                              <div>
                                <Badge variant="outline" className={isAhp ? "bg-[#2b5e49]/10 text-[#2b5e49] border-[#2b5e49]/30 font-semibold" : "bg-green-900 text-green-300 border-green-600"}>
                                  {t('codes:shared.verified')}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div className={`mt-4 p-4 ${sectionBg} border rounded-lg`}>
                        <h4 className={`font-semibold mb-2 ${sectionText}`} style={headingFont}>{t('codes:shared.keyInsights')}</h4>
                        <ul className={`text-sm space-y-1 ${sectionSubtext}`}>
                          <li>• {t('codes:shared.insightUsHigher')}</li>
                          <li>• {t('codes:shared.insightSinglePayer')}</li>
                          <li>• {t('codes:shared.insightDataSources')}</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              );
            })()}

            {/* Analytics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle style={headingFont}>{t('codes:shared.priceStatistics')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span>{t('codes:shared.minimumPrice')}:</span>
                    <span className="font-semibold">${Math.round(selectedCode.min_gross_charge || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('codes:shared.maximumPrice')}:</span>
                    <span className="font-semibold">${Math.round(selectedCode.max_gross_charge || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('codes:shared.medianPrice')}:</span>
                    <span className="font-semibold">${Math.round(selectedCode.median_gross_charge || 0).toLocaleString()}</span>
                  </div>
                  {selectedCode.avg_discounted_cash > 0 && (
                  <div className="flex justify-between">
                    <span>{t('codes:shared.averageCashPrice')}:</span>
                    <span className="font-semibold">${Math.round(selectedCode.avg_discounted_cash).toLocaleString()}</span>
                  </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle style={headingFont}>{t('codes:shared.marketAnalysis')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">
                      {selectedCode.price_variation?.toFixed(1)}%
                    </p>
                    <p className="text-sm text-muted-foreground">{t('codes:shared.priceVariation')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">
                      {((selectedCode.max_gross_charge / selectedCode.min_gross_charge) || 0).toFixed(1)}x
                    </p>
                    <p className="text-sm text-muted-foreground">{t('codes:shared.priceMultiplier')}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : !code ? (
          // Code List View - only show when no specific code is requested
          <Card>
            <CardHeader>
              <CardTitle>{t('codes:shared.mostCommonCodes', { codeType: config.pluralName })}</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('codes:shared.code')}</TableHead>
                    <TableHead>{t('codes:shared.description')}</TableHead>
                    <TableHead>{t('codes:shared.hospitals')}</TableHead>
                    <TableHead>{t('codes:shared.avgPrice')}</TableHead>
                    <TableHead>{t('codes:shared.priceRange')}</TableHead>
                    <TableHead>{t('codes:shared.variation')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCodes.map((code) => (
                    <TableRow
                      key={code.procedure_code}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => window.location.href = `/cpt/${code.procedure_code}`}
                    >
                      <TableCell className="font-medium">{code.procedure_code}</TableCell>
                      <TableCell className="max-w-md truncate">{code.procedure_description}</TableCell>
                      <TableCell>{code.total_hospitals}</TableCell>
                      <TableCell>${code.avg_gross_charge?.toLocaleString()}</TableCell>
                      <TableCell>
                        ${code.min_gross_charge?.toLocaleString()} - ${code.max_gross_charge?.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant={code.price_variation > 100 ? "destructive" : "secondary"}>
                          {code.price_variation?.toFixed(1)}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          // Code specified but no pricing data - show educational content if available
          <>
            <Card>
              <CardContent className="py-8">
                <div className="text-center text-muted-foreground">
                  <Info className="w-12 h-12 mx-auto mb-4 text-blue-500" />
                  <h3 className="text-lg font-semibold mb-2">{t('codes:shared.pricingDataNotAvailable')}</h3>
                  <p>{t('codes:shared.noDataForCode', { codeType: config.displayName, code })}</p>
                </div>
              </CardContent>
            </Card>

            {/* Educational Content - Show even without pricing data */}
            {educationalHtml && (
              <div className="w-full mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2" style={headingFont}>
                      <Info className="w-5 h-5" style={isAhp ? { color: heroAccentColor } : undefined} />
                      {t(`codes:${i18nCodeType}.aboutCode`, { code })}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="overflow-x-hidden break-words">
                    <div className={`prose ${isAhp ? 'prose-stone' : 'prose-invert'} max-w-none [&>*]:break-words [&>*]:overflow-wrap-anywhere`}>
                      <EducationalContentRenderer
                        htmlContent={educationalHtml}
                        procedureCode={code || ''}
                        codeType={config.codeTypeUpper}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
        </div>

        {/* Share modal — wired to both right-click anywhere on the page (above) +
            the visible Share button in the page header. Pre-loaded with this code's
            context so all templates generate code-aware social copy. */}
        {selectedCode && (
          <ShareDignityModal
            isOpen={shareOpen}
            onClose={() => setShareOpen(false)}
            shareUrl={typeof window !== 'undefined' ? window.location.href : undefined}
            code={selectedCode.procedure_code}
            codeType={config.codeType}
            codeTypeLabel={config.codeTypeUpper}
          />
        )}
      </div>
    </TooltipProvider>
  );
};

// Export with original name for backwards compatibility
export default ProcedureCodePage;

// Named export for new usage
export { ProcedureCodePage };